// Presentation detail / builder — Phase 7.5.4.A.
//
// The rep-side authoring surface for product presentations. Sections:
//   1. Header — presentation #, status, project + client link, copy
//      share link button, send-to-client button, delete.
//   2. Edit form — introduction + in_hands_date + layout + hide_pricing.
//   3. Items list — each item: thumbnail + name + supplier + position
//      controls (up/down) + remove. Empty state has a CTA to "Add a
//      product".
//   4. "Add a product" modal — searches the public /storefront/products
//      endpoint so client_rep + admin both work (no admin-only gate).
//      Pick a row → resolve preferred supplier id via /storefront/products/:id
//      → POST to /presentations/:id/items.
//
// Pricing tiers (Phase 7.5.4.B): each item has its own quantity-bracket
// table — quantity / wholesale / sell / margin / hide-from-client. Reps
// add/edit/remove rows inline. The public client view only sees the
// `quantity` + `sellPrice` fields (margin + wholesale + isHiddenFromClient
// are stripped by the route mapper).
//
// Pattern: TanStack Query for fetch + useMutation per action, brand-
// locked palette, no Hook Form, no DnD library (up/down arrows handle
// reorder for now).

import { CustomProductForm, type CreatedCustomProduct } from "@/components/catalog/CustomProductForm";
import { useEffect, useMemo, useRef, useState } from "react";
import { ItemVirtualsStrip, MakeVirtualsDialog, PresentationVirtualsProvider, usePresentationLatestSet } from "@/components/virtuals/MakeVirtualsDialog";
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  GripVertical,
  Check,
  Copy,
  CopyPlus,
  ExternalLink,
  Pencil,
  Plus,
  Send,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  X,
} from "lucide-react";
import { groupItemsForBuilder } from "@merchd/shared";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { StatusPill } from "@/components/ui/StatusPill";
import { AddProductModal } from "@/components/catalog/AddProductModal";
import { useAuth } from "@/auth/AuthProvider";
import { ApiError, api } from "@/lib/api";
import { friendlyApiError } from "@/lib/apiErrorCopy";
import { cn } from "@/lib/utils";
import { canCreatePresentationAtStage } from "@/lib/projectStage";
import {
  useCreatePresentation,
  createPresentationErrorMessage,
} from "@/lib/useCreatePresentation";
import type { ProjectDetailResponse } from "@/pages/ProjectDetailPage";
import {
  type Decoration,
  DecorationSection,
} from "./PresentationDecorationSection";
import {
  type ItemCharge,
  ItemChargesSection,
} from "./PresentationItemChargesSection";
import {
  type CloneSourceItem,
  type CloneTargetSubmit,
  CloneTargetModal,
} from "@/components/clone/CloneTargetModal";
import {
  type ConvertSelectionResult,
  type ConvertSourceItem,
  ConvertItemSelectionModal,
} from "@/components/convert/ConvertItemSelectionModal";
// Cost validation + 3dp-aware display come from the SHARED money module, not a
// local regex — a hand-written copy is how the tier rows drifted from the API.
import {
  copyCostWrites,
  formatMills,
  isValidCostInput,
  planTierBatch,
  type TierBatchError,
  type TierDraft,
  toMills,
} from "@/components/documents/money";
import { LiveCell } from "@/components/product-edit/LiveCell";
import { ProductEditModal } from "@/components/product-edit/ProductEditModal";
import {
  SendToCustomerModal,
  type SendRecipientInput,
} from "@/components/send/SendToCustomerModal";

type PresentationStatus =
  | "draft"
  | "open"
  | "sent"
  | "viewed"
  | "comments_pending"
  | "approved"
  | "rejected";

type PresentationLayout = "list" | "grid" | "spotlight" | "compact" | "minimal" | "cards";

export interface PresentationItemTier {
  id: string;
  presentationItemId: string;
  quantity: number;
  wholesaleCost: string;
  sellPrice: string;
  marginPct: string | null;
  isCustom: boolean | null;
  position: number;
  isHiddenFromClient: boolean | null;
}

/** A titled group inside a presentation. Created blank, titled in place. */
export interface PresentationSection {
  id: string;
  presentationId: string;
  title: string;
  position: number;
  createdAt: string | null;
}

export interface PresentationItem {
  id: string;
  presentationId: string;
  productId: string;
  productSnapshot: {
    name?: string;
    imageUrls?: string[] | Record<string, string>;
    categories?: string[];
    vendorId?: string;
    sku?: string;
    description?: string;
    basePriceMin?: string;
    basePriceMax?: string;
  } | null;
  /** Supplier name, joined live server-side (2026-08-11). Null when the vendor
   *  row is gone. Not from the snapshot — that carries a UUID. */
  vendorName?: string | null;
  /** When non-null, the client sees this; when null, UI falls back to
   *  productSnapshot.description. */
  descriptionOverride: string | null;
  /** Titled group this item sits under. null = ungrouped, which renders first
   *  and without a header — the shape every pre-2026-08-06 presentation has. */
  sectionId: string | null;
  position: number;
  isHidden: boolean | null;
  createdAt: string | null;
  tiers: PresentationItemTier[];
  decorations: Decoration[];
  itemCharges?: ItemCharge[];
}

interface PresentationDetail {
  id: string;
  presentationNumber: number;
  projectId: string;
  status: PresentationStatus;
  publicShareUuid: string;
  hidePricing: boolean | null;
  layout: PresentationLayout | null;
  introduction: string | null;
  inHandsDate: string | null;
  sentAt: string | null;
  firstViewedAt: string | null;
  lastViewedAt: string | null;
  viewCount: number | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface ClientFeedbackSubmission {
  submissionId: string;
  viewerEmail: string | null;
  likes: number;
  dislikes: number;
  comment: string | null;
  submittedAt: string;
}

interface ClientItemComment {
  presentationItemId: string;
  comment: string;
  viewerEmail: string | null;
  submittedAt: string;
}

interface ClientFeedbackSummary {
  thumbsByItem: Array<{
    presentationItemId: string;
    likes: number;
    dislikes: number;
  }>;
  // 2026-07-22 — per-item notes from the redesigned share page (capped at 50
  // newest; itemCommentsTotal is the full count for the "+N earlier" line).
  itemComments: ClientItemComment[];
  itemCommentsTotal?: number;
  // 2026-07-26 (B6) — the LATEST submission's item picks. null when the
  // client never selected. Drives the "Client's picks" line + convert
  // pre-check.
  clientSelections?: {
    submissionId: string;
    viewerEmail: string | null;
    submittedAt: string;
    presentationItemIds: string[];
    submissionCount: number;
  } | null;
  recentSubmissions: ClientFeedbackSubmission[];
  totalLikes: number;
  totalDislikes: number;
  lastSubmittedAt: string | null;
}

interface DetailResponse {
  presentation: PresentationDetail;
  items: PresentationItem[];
  feedback?: ClientFeedbackSummary;
  /** Was a client-facing email ever actually sent (vs a link-only publish)?
   *  Derived server-side from email_sends. Keeps the status honest on reload. */
  email_sent?: boolean;
}

const STATUS_TONES: Record<
  PresentationStatus,
  "active" | "prospect" | "dormant" | "closed" | "warn" | "info"
> = {
  draft: "info",
  open: "info",
  sent: "prospect",
  viewed: "prospect",
  comments_pending: "warn",
  approved: "active",
  rejected: "warn",
};

const STATUS_LABELS: Record<PresentationStatus, string> = {
  draft: "Draft",
  open: "Open",
  sent: "Sent",
  viewed: "Viewed",
  comments_pending: "Client notes",
  approved: "Approved",
  rejected: "Rejected",
};

// CEO 2026-08-04 supersedes the round-59 opener-only default: the send dialog now
// pre-fills the WHOLE email from GET /presentations/:id/send-compose (rendered by
// the same server template the send route uses) and every word of it is editable.
// There is no client-side default string any more — duplicating the copy here is
// exactly the drift the compose endpoint exists to prevent.

export function PresentationDetailPage() {
  // Presentations are viewed INSIDE their project sub-nav at
  // /projects/:id/presentation/:presentationId (2026-06-01 nav follow-up
  // to Nav Part 1). The legacy route `/presentations/:id` survives via
  // PresentationRedirect for global-search / bookmark links. Accept
  // either param so the same component renders both URLs.
  const params = useParams<{ presentationId?: string; id?: string }>();
  const id = params.presentationId ?? params.id;
  const navigate = useNavigate();
  // This page nests inside ProjectDetailLayout's <Outlet>, which passes the
  // project-detail payload as context. Read the project's stage to gate the
  // in-context "New presentation" affordance (the escape hatch the removed
  // Actions ▾ dropdown used to provide when the Presentation tab auto-opens a
  // single presentation). Optional — the legacy /presentations/:id redirect
  // shim renders through the same layout, but guard for safety.
  const projectCtx = useOutletContext<ProjectDetailResponse | undefined>();
  const { user } = useAuth();
  const canEdit =
    user?.roles.some((r) => r === "admin" || r === "client_rep") ?? false;
  const queryClient = useQueryClient();

  // In-context "New presentation" — the SAME hook the Presentation tab uses, so
  // the two create paths can't drift. Stage-gated below to opportunity/
  // presentation, mirroring the getAvailableActions rule engine. projectId comes
  // from the outlet context (always present under the project layout).
  const createPresentation = useCreatePresentation(projectCtx?.project.id ?? "");
  const createPresentationError = createPresentationErrorMessage(
    createPresentation.error,
  );

  const detailKey = useMemo(
    () => ["presentations", "detail", id] as const,
    [id],
  );

  const detailQ = useQuery({
    queryKey: detailKey,
    queryFn: () => api<DetailResponse>(`/presentations/${id}`),
    enabled: Boolean(id),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: detailKey });
    void queryClient.invalidateQueries({ queryKey: ["presentations", "list"] });
  };

  if (detailQ.isLoading) {
    return (
      <p className="mx-auto max-w-none text-sm text-brown-300">Loading…</p>
    );
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <p className="mx-auto max-w-none text-sm text-rust-600">
        Could not load this presentation.{" "}
        <Link
          to="/projects"
          className="underline hover:text-brown-500"
        >
          Back to projects
        </Link>
        .
      </p>
    );
  }

  const { presentation, items, feedback } = detailQ.data;
  const emailSent = detailQ.data.email_sent ?? false;
  const sortedItems = [...items].sort((a, b) =>
    a.position - b.position || a.id.localeCompare(b.id),
  );

  // Project-only access (2026-06-01 follow-up to Nav Part 1+2): the
  // back affordance points to THIS presentation's project, not the
  // global list. Matches OrderDetailPage's `← Estimates` /
  // `← Quotes` breadcrumb shape so the in-project surfaces feel
  // consistent.
  const backToProjectPresentationsHref = `/projects/${presentation.projectId}/presentation`;

  // Escape hatch for the single-presentation auto-open trap (P1): when the
  // Presentation tab holds exactly one presentation it redirects straight here,
  // and the "← Presentations" breadcrumb just redirects back — so without this
  // there was no way to start a SECOND presentation once the header dropdown was
  // removed. Gate to the stages the rule engine offers create_presentation.
  const showNewPresentation =
    projectCtx != null &&
    canCreatePresentationAtStage(projectCtx.project.statusStage);

  return (
    <div className="mx-auto max-w-none space-y-6">
      {/* Page-level harmonization (CEO 2026-07-29 "moving between the
          presentation and estimate shouldn't be such a big UI transition"): the
          standalone "← Presentations" back-link band was dropped to match the
          estimate/SO density pass. Both surfaces nest in ProjectDetailLayout,
          whose breadcrumb + left sub-nav already carry the client/project
          context and a Presentation tab back to the list — the band was pure
          duplication (mirror of the estimate's dropped back-link,
          OrderDetailPage.tsx:1311-1316). The one legitimate affordance it held
          — the "New presentation" escape hatch — moves INTO the header actions
          cluster as a trailing ghost, exactly where the estimate keeps its
          "New estimate" ghost (OrderDetailPage.tsx:1521-1544).

          RESTORED 2026-08-01: this harmonization shipped in round 57 (7b78efd)
          and was silently REVERTED by round 59's assembly (51149a5), whose
          editable-send-email branch was cut from a pre-harmonization base and
          resolved four stacked-squash conflicts keep-theirs. It rode live for
          three rounds; qa-presentation-builder flagged it the whole time and
          was read as a stale red. Do not re-flatten this header without a CEO
          direction — the estimate parity IS the requirement. */}
      <HeaderSection
        presentation={presentation}
        items={sortedItems}
        emailSent={emailSent}
        canEdit={canEdit}
        clientSelectedItemIds={feedback?.clientSelections?.presentationItemIds ?? null}
        onMutated={invalidate}
        onDeleted={() => navigate(backToProjectPresentationsHref)}
        showNewPresentation={showNewPresentation}
        onNewPresentation={() => createPresentation.mutate()}
        newPresentationPending={createPresentation.isPending}
        newPresentationError={createPresentationError}
      />

      {feedback &&
        (feedback.recentSubmissions.length > 0 ||
          (feedback.itemComments?.length ?? 0) > 0 ||
          feedback.clientSelections != null) && (
          <ClientFeedbackPanel feedback={feedback} items={sortedItems} />
        )}

      <EditForm
        presentation={presentation}
        canEdit={canEdit}
        onMutated={invalidate}
      />

      <ItemsSection
        presentationId={presentation.id}
        items={sortedItems}
        canEdit={canEdit}
        onMutated={invalidate}
      />
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────

function HeaderSection(props: {
  presentation: PresentationDetail;
  items: PresentationItem[];
  emailSent: boolean;
  canEdit: boolean;
  /** 2026-07-26 (B6) — the client's latest item picks, or null. Seeds the
   *  convert picker's pre-check. */
  clientSelectedItemIds: string[] | null;
  onMutated: () => void;
  onDeleted: () => void;
  /** Harmonization (2026-07-30): the "New presentation" escape hatch, relocated
   *  from the dropped back-link band into the header actions cluster — parity
   *  with the estimate's trailing "New estimate" ghost. Stage-gated by the
   *  caller (canCreatePresentationAtStage). */
  showNewPresentation: boolean;
  onNewPresentation: () => void;
  newPresentationPending: boolean;
  newPresentationError: string | null;
}) {
  const {
    presentation: p,
    items,
    emailSent,
    canEdit,
    clientSelectedItemIds,
    onMutated,
    onDeleted,
    showNewPresentation,
    onNewPresentation,
    newPresentationPending,
    newPresentationError,
  } = props;
  const [error, setError] = useState<string | null>(null);
  // Set when a link-only publish succeeded — honest confirmation copy.
  const [linkOnlyNotice, setLinkOnlyNotice] = useState<string | null>(null);
  // Set when the send hit 409 no_client_contact — drives the add-contact /
  // share-link-only prompt instead of a plain error string.
  const [noContactPrompt, setNoContactPrompt] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);
  // Drives the multi-recipient send window (reused from estimate/SO). Set when
  // the rep clicks "Send to client".
  const [showSendModal, setShowSendModal] = useState(false);
  // The logged-in user, so the dialog can explain a "Sending as" line that names
  // the ACCOUNT REP rather than whoever is clicking Send.
  const { user } = useAuth();
  const [sendModalError, setSendModalError] = useState<string | null>(null);
  // Missing-link soft gate (CEO 2026-08-04). Same shape as the PO
  // $0-decoration confirm: a peach NOTICE (never the rust error slot), the CTA
  // flips to "Send anyway", and a second click carries the skip flag.
  const [sendModalNotice, setSendModalNotice] = useState<string | null>(null);
  const [missingLinkConfirmPending, setMissingLinkConfirmPending] =
    useState(false);
  const [sentNotice, setSentNotice] = useState<string | null>(null);

  // Lazy-load the source project's client + name when the rep opens the
  // Clone modal — saves a per-page roundtrip when Clone isn't used. Also
  // resolves the client id for the no-contact prompt's "Add contact" link.
  const projectDetailQ = useQuery({
    queryKey: ["projects", p.projectId, "detail-for-clone"],
    queryFn: () =>
      api<{
        project: { id: string; name: string };
        client: { id: string; name: string };
      }>(`/projects/${p.projectId}/detail`),
    enabled: showCloneModal || noContactPrompt || showSendModal,
  });

  const send = useMutation({
    // Multi-recipient send (2026-07-23): the rep picks who gets the /p/:uuid
    // link — client contacts + ad-hoc emails + an optional copy to themselves —
    // mirroring the estimate/SO machinery. `recipients` omitted / link-only →
    // the legacy single-primary-contact fallback stays intact.
    mutationFn: async (opts?: {
      publishLinkOnly?: boolean;
      recipients?: SendRecipientInput[];
      sendCopyToMe?: boolean;
      /** The BCC box (Stephanie 2026-11-04). */
      bcc?: Array<{ email: string; name?: string }>;
      /** The rep's FULL edited email (CEO 2026-08-04) — replaces the template. */
      bodyHtml?: string;
      /** Second click on the missing-link soft gate → send anyway. */
      skipMissingLinkCheck?: boolean;
    }) =>
      api<{
        presentation: PresentationDetail;
        public_url: string;
        email_sent: boolean;
        mode?: "email" | "link_only";
        recipients?: string[];
        email_skip_reason?: string;
      }>(`/presentations/${p.id}/send`, {
        method: "POST",
        body: JSON.stringify(
          opts?.publishLinkOnly
            ? { publish_link_only: true }
            : // When the picker was used we ALWAYS send the explicit array
              // (including []) so the backend never falls back to the primary
              // behind an explicit deselection. `recipients` is omitted only for
              // a genuine legacy call (no picker) — mirrors OrderDetailPage.
              opts?.recipients
              ? {
                  recipients: opts.recipients,
                  send_copy_to_me: opts?.sendCopyToMe ?? false,
                  ...(opts.bcc && opts.bcc.length > 0 ? { bcc: opts.bcc } : {}),
                  // The rep's WHOLE email. Sanitised server-side to the strict
                  // allowlist before it is stored or sent.
                  ...(opts.bodyHtml !== undefined
                    ? { body_html: opts.bodyHtml }
                    : {}),
                  ...(opts.skipMissingLinkCheck
                    ? { skip_missing_link_check: true }
                    : {}),
                }
              : {},
        ),
      }),
    onError: (err) => {
      // 409 no_client_contact isn't a plain error — it's a decision point.
      // Surface the add-contact / share-link-only prompt instead of a red line.
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        (err.body as { error?: string } | null)?.error === "no_client_contact"
      ) {
        setError(null);
        setLinkOnlyNotice(null);
        setShowSendModal(false);
        setNoContactPrompt(true);
        return;
      }
      // Missing-link soft gate (409). Keep the dialog OPEN so the rep's
      // recipients + body survive, arm the skip flag, and route the copy through
      // the PEACH notice slot — a confirmation prompt must never wear failure
      // styling (round-61 convention).
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        (err.body as { reason?: string } | null)?.reason ===
          "missing_document_link"
      ) {
        setMissingLinkConfirmPending(true);
        setSendModalError(null);
        setSendModalNotice(
          (err.body as { message?: string } | null)?.message ??
            "This email doesn't include the presentation link — the client won't be able to open it. Send anyway?",
        );
        return;
      }
      // A foreign / stale contact id (403) — show it inside the modal.
      if (
        err instanceof ApiError &&
        err.status === 403 &&
        (err.body as { error?: string } | null)?.error === "foreign_contact"
      ) {
        setSendModalError(
          "One of the selected contacts no longer belongs to this client. Refresh and try again.",
        );
        return;
      }
      setNoContactPrompt(false);
      if (showSendModal) {
        setSendModalError(extractError(err));
        return;
      }
      setError(extractError(err));
    },
    onSuccess: (result) => {
      setError(null);
      setNoContactPrompt(false);
      setSendModalError(null);
      setSendModalNotice(null);
      setMissingLinkConfirmPending(false);
      setShowSendModal(false);
      // Link-only publish: the share link is live but nobody was emailed —
      // say so plainly (never "sent to the client").
      if (!result.email_sent && result.mode === "link_only") {
        setLinkOnlyNotice(
          "Link shared — no email sent. Copy the link above to give it to the client.",
        );
        setSentNotice(null);
      } else {
        setLinkOnlyNotice(null);
        const who =
          result.recipients && result.recipients.length > 0
            ? result.recipients.join(", ")
            : null;
        setSentNotice(
          who ? `Sent to ${who}.` : "Sent — the client was emailed the link.",
        );
      }
      onMutated();
    },
  });

  const del = useMutation({
    mutationFn: async () =>
      api(`/presentations/${p.id}`, { method: "DELETE" }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => onDeleted(),
  });

  // Clone the presentation (re-order primitive). Modal lets the rep
  // pick a target client + new project name + which items to copy
  // (matches commonsku's Copy modal). PR #135. POs/bills/invoices/
  // commissions are NEVER cloned per operator constraint (PR #131).
  const navigateRef = useNavigate();
  // After convert / clone, the project's quote list and counts and the
  // account's project list show the change (Stephanie 2026-11-02 sweep).
  const headerQc = useQueryClient();
  const refreshProjectLists = (): void => {
    void headerQc.invalidateQueries({ queryKey: ["merchd.projectDetail"] });
    void headerQc.invalidateQueries({ queryKey: ["project"] });
    void headerQc.invalidateQueries({ queryKey: ["clients"] });
  };
  const clone = useMutation({
    mutationFn: async (next: CloneTargetSubmit) =>
      api<{
        new_presentation_id: string;
        new_presentation_number: number;
        source_presentation_number: number;
        new_project_id: string | null;
        items_cloned: number;
        decorations_cloned: number;
      }>(`/presentations/${p.id}/clone`, {
        method: "POST",
        body: JSON.stringify({
          new_project: {
            client_id: next.clientId,
            name: next.projectName,
          },
          selected_item_ids: next.selectedIds,
        }),
      }),
    onError: (err) => setCloneError(extractError(err)),
    onSuccess: (result) => {
      setCloneError(null);
      setShowCloneModal(false);
      refreshProjectLists();
      // Land in the new presentation's project sub-nav. Clone always
      // creates a new project (PR #135) so `new_project_id` is set; fall
      // back to the redirect path defensively if a future code path
      // returns null.
      if (result.new_project_id) {
        navigateRef(
          `/projects/${result.new_project_id}/presentation/${result.new_presentation_id}`,
        );
      } else {
        navigateRef(`/presentations/${result.new_presentation_id}`);
      }
    },
  });

  // Convert flow (2026-05-26 — operator-locked commonsku-style flow):
  // clicking Convert opens the ItemSelectionModal. The rep picks which
  // items + per-item qty; submit hits the convert route with body
  // `{ selected_item_ids, item_quantities }`. The same modal serves
  // Estimate and Quote — only the target differs.
  type ConvertTarget = "estimate" | "sales_order";
  const [convertTarget, setConvertTarget] = useState<ConvertTarget | null>(
    null,
  );
  const [convertError, setConvertError] = useState<string | null>(null);
  /** Non-fatal outcome of the last convert — e.g. "2 items didn't convert".
   *  Separate from convertError: the conversion SUCCEEDED, it just did less
   *  than the rep asked for, and that must be said rather than inferred. */
  const [convertNotice, setConvertNotice] = useState<string | null>(null);
  /** Set alongside convertNotice — the document that WAS created, so the rep can
   *  open it once she has read what was left behind. */
  const [convertedOrderId, setConvertedOrderId] = useState<string | null>(null);
  const convert = useMutation({
    mutationFn: async (body: {
      target: ConvertTarget;
      selected_item_ids: string[];
      item_quantities: Record<string, number>;
    }) => {
      const conv = await api<{ orderId: string; linesCreated: number }>(
        `/presentations/${p.id}/convert`,
        {
          method: "POST",
          body: JSON.stringify({
            selected_item_ids: body.selected_item_ids,
            item_quantities: body.item_quantities,
          }),
        },
      );
      if (body.target === "sales_order") {
        await api(`/orders/${conv.orderId}/convert-to-sales-order`, {
          method: "POST",
        });
      }
      // 🔴 Carry the REQUESTED count through so onSuccess can compare it with
      // what actually landed. See the note there.
      return { ...conv, requested: body.selected_item_ids.length };
    },
    onError: (err) => setConvertError(extractError(err)),
    onSuccess: (result) => {
      setConvertError(null);
      setConvertTarget(null);
      refreshProjectLists();
      // 🔴 A SKIPPED ITEM MUST NOT BE SILENT (2026-08-11).
      //
      // convertPresentationToOrder skips any item with no priced tier
      // (`if (!chosenTier) continue`, orders-builder.ts) and returns a lower
      // linesCreated. Nothing compared the two, so the rep selected five items,
      // landed on an estimate holding four, and was told nothing — no error, no
      // count, no reason.
      //
      // That path became REACHABLE the moment manual-entry cost was made
      // optional in this same branch: "leave the cost blank" is now advice we
      // give, and an uncosted item is exactly what convert drops. Making the
      // gap loud is part of that change, not a separate nicety.
      const missing = result.requested - result.linesCreated;
      if (missing > 0) {
        // Deliberately DO NOT navigate. A toast on a page we are leaving is a
        // toast nobody reads — and the whole failure here was silence. Hold the
        // rep on the presentation, name what was dropped, and let her open the
        // document when she has seen it.
        setConvertNotice(
          `${missing} of ${result.requested} item${result.requested === 1 ? "" : "s"} didn't convert — ` +
            `those items have no pricing set. Add a price tier on each, then convert them separately.`,
        );
        setConvertedOrderId(result.orderId);
        return;
      }
      navigateRef(`/orders/${result.orderId}`);
    },
  });

  const convertSourceItems: ConvertSourceItem[] = items
    .filter((it) => !it.isHidden)
    .map((it) => {
      const snap = it.productSnapshot as
        | { name?: string; imageUrls?: string[] | Record<string, string> }
        | null;
      let imageUrl: string | null = null;
      const images = snap?.imageUrls;
      if (Array.isArray(images) && images.length > 0) {
        imageUrl = images[0] ?? null;
      } else if (images && typeof images === "object") {
        const first = Object.values(images)[0];
        imageUrl = typeof first === "string" ? first : null;
      }
      const firstDeco = (it.decorations ?? [])[0];
      const decoParts = firstDeco
        ? [firstDeco.location?.trim() || null, firstDeco.logoName?.trim() || null].filter(
            Boolean,
          )
        : [];
      return {
        id: it.id,
        name: snap?.name ?? "Untitled product",
        imageUrl,
        defaultQty: firstTierQty(it.tiers),
        // Same label the item card and the client view use — see ItemCard.
        decoLabel: decoParts.length > 0 ? decoParts.join(" · ") : null,
        // Zero tiers = the converter skips this item silently. Surfaced on the
        // row so the rep sees it BEFORE converting.
        unpriced: (it.tiers?.length ?? 0) === 0,
      };
    });

  const shareUrl = `${window.location.origin}/p/${p.publicShareUuid}`;
  // Send is allowed from any non-terminal status. draft/open = initial
  // send; sent/viewed/comments_pending = resend (bumps sentAt + re-emails).
  // approved/rejected are terminal — those flow to Estimate / SO, not
  // another send.
  const isSendable =
    p.status !== "approved" && p.status !== "rejected";
  const isResend =
    p.status === "sent" ||
    p.status === "viewed" ||
    p.status === "comments_pending";
  // Convert is allowed any time the presentation has items. The
  // backend rejects 409 if no items have a qualifying tier.
  const hasVisibleItems = items.some((it) => !it.isHidden);

  // Stage-derived primary CTA (UX S3): the header had seven equal buttons.
  // draft/open/sent/viewed → Send (initial send or Resend) is primary. Once the
  // presentation is TERMINAL (approved OR rejected), Send is disabled, so the
  // forward step "Convert to Estimate" becomes primary instead of leaving a
  // greyed Send as the visual anchor (UX S2 / code-review REJECT-2 — one fix
  // satisfies both). Convert-to-Sales-Order always stays secondary; Delete is
  // ghost-tier; Clone stays outline (parity with OrderDetailPage's Clone).
  // Hierarchy change only — every action stays reachable.
  const primaryAction: "send" | "convert_estimate" =
    p.status === "approved" || p.status === "rejected"
      ? "convert_estimate"
      : "send";

  // "Copied!" feedback state lingers 2s then resets (operator feedback
  // 2026-05-26 — no indicator was confusing).
  const [copiedAt, setCopiedAt] = useState<number | null>(null);
  const copied = copiedAt !== null && Date.now() - copiedAt < 2000;
  useEffect(() => {
    if (copiedAt === null) return;
    const t = setTimeout(() => setCopiedAt(null), 2100);
    return () => clearTimeout(t);
  }, [copiedAt]);

  const copyShareLink = async (): Promise<void> => {
    setError(null);
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedAt(Date.now());
    } catch {
      setError("Could not copy — your browser blocked clipboard access.");
    }
  };

  // Published link-only: the link is live but no email was ever sent. The
  // "Sent" badge would imply the client was emailed — say "Link shared"
  // instead. Later statuses (viewed/approved/…) reflect real client actions,
  // so only the ambiguous "Sent" badge is overridden.
  const showLinkOnlyBadge = p.status === "sent" && !emailSent;

  return (
    // Header band + its notice/share-link cluster. The band carries the
    // estimate's mb-5 rhythm directly (OrderDetailPage.tsx:1317) and the cluster
    // below keeps its own space-y-4 — replacing the old space-y-4-on-the-root
    // that made the header→content gap 16px, 4px short of the estimate's 20px
    // (ux + ui 2026-07-30). Goal: identical header spacing math across surfaces.
    <div>
      {/* Header band — harmonized to the estimate/SO grammar (CEO 2026-07-25
          density pass, OrderDetailPage.tsx:1317-1351): eyebrow · #number ·
          StatusPill sit INLINE on one compact row, actions on the right, rather
          than the old stacked eyebrow / h1 / pill-row. Presentation-specific
          metadata (in-hands · sent · views) STAYS — it trails the pill inline in
          the same wrap cluster (harmonize the frame, keep the function). */}
      {/* Header (other Business UI handoff, W10) — the estimate/SO grammar:
          eyebrow · P-number at 32px · status, actions on the right with ONE
          dominant action (Send while open, Convert once decided). The
          presentation's facts sit in a meta row underneath. */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            Presentation
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
              P-{p.presentationNumber}
            </h1>
            <StatusPill tone={showLinkOnlyBadge ? "warn" : STATUS_TONES[p.status]}>
              {showLinkOnlyBadge
                ? "Link shared — no email sent"
                : STATUS_LABELS[p.status]}
            </StatusPill>
          </div>
          <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
            <HeaderFact label="In hands" value={p.inHandsDate ? formatDate(p.inHandsDate) : "Not set"} />
            <HeaderFact label="Products" value={String(items.filter((it) => !it.isHidden).length)} />
            <HeaderFact
              label={emailSent || !p.sentAt ? "Sent" : "Link shared"}
              value={p.sentAt ? formatDate(p.sentAt.slice(0, 10)) : "Not yet"}
            />
            <HeaderFact
              label="Client views"
              value={p.viewCount && p.viewCount > 0 ? p.viewCount.toLocaleString() : "None yet"}
            />
          </dl>
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={() => {
                setCloneError(null);
                setShowCloneModal(true);
              }}
              title="Clone this presentation into a new project. Pick a target client + name; items + decorations + charges carry forward."
            >
              <CopyPlus className="h-3.5 w-3.5" /> Clone
            </Button>
            {/* One "Create quote" (CEO 2026-10-15): Estimate + Sales Order are
                one Quote; the client's approval puts it live. */}
            <Button
              type="button"
              variant={primaryAction === "convert_estimate" ? "primary" : "outline"}
              size="md"
              disabled={!hasVisibleItems || convert.isPending}
              onClick={() => {
                setConvertError(null);
                setConvertTarget("estimate");
              }}
              title={
                hasVisibleItems
                  ? "Create a quote from this presentation — pick which products and per-item quantity in the next dialog"
                  : "Add at least one visible item first"
              }
            >
              <ArrowRight className="h-3.5 w-3.5" /> Create quote
            </Button>
            <Button
              type="button"
              variant={primaryAction === "send" ? "primary" : "outline"}
              size="md"
              disabled={!isSendable || send.isPending}
              onClick={() => {
                setError(null);
                setNoContactPrompt(false);
                setSendModalError(null);
                setSentNotice(null);
                setShowSendModal(true);
              }}
              title={
                isSendable
                  ? isResend
                    ? "Resend the presentation — re-email the client (and anyone you copy) and stamp a new sent-at"
                    : "Send the presentation to the client (and anyone you'd like to copy)"
                  : "Closed — approved / rejected presentations move on to a quote"
              }
            >
              <Send className="h-3.5 w-3.5" />{" "}
              {send.isPending
                ? isResend
                  ? "Resending…"
                  : "Sending…"
                : isResend
                  ? "Resend"
                  : "Send to client"}
            </Button>
            {!confirmingDelete && (
              <Button
                type="button"
                variant="ghost"
                size="md"
                onClick={() => setConfirmingDelete(true)}
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </Button>
            )}
            {confirmingDelete && (
              <>
                <Button
                  type="button"
                  variant="risk"
                  size="md"
                  disabled={del.isPending}
                  onClick={() => del.mutate()}
                >
                  Confirm delete
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={del.isPending}
                >
                  Cancel
                </Button>
              </>
            )}
            {/* New presentation (escape hatch) — relocated from the dropped
                back-link band. Rendered LAST as a ghost, mirroring the
                estimate's trailing "New estimate" ghost
                (OrderDetailPage.tsx:1530-1544): starting a second presentation
                is a low-frequency side path, never leading the stage's primary
                Send/Convert CTA. Stage-gated by the caller. */}
            {showNewPresentation && (
              <Button
                type="button"
                variant="ghost"
                size="md"
                onClick={onNewPresentation}
                disabled={newPresentationPending}
                title="Start another presentation on this project."
              >
                <Plus className="h-3.5 w-3.5" />
                {newPresentationPending ? "Creating…" : "New presentation"}
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="space-y-4">
        {newPresentationError && (
          <p role="alert" className="text-sm text-risk">{newPresentationError}</p>
        )}
        {error && <p role="alert" className="text-sm text-risk">{error}</p>}
        {linkOnlyNotice && (
          <p role="status" className="rounded-md border border-warn/30 bg-warn-bg px-4 py-3 text-sm text-warn">
            {linkOnlyNotice}
          </p>
        )}
        {sentNotice && (
          <p role="status" className="rounded-md border border-ok/30 bg-ok-bg px-4 py-3 text-sm text-ok">
            {sentNotice}
          </p>
        )}
        {noContactPrompt && (
          <div className="rounded-md border border-warn/30 bg-warn-bg px-4 py-3 text-sm text-ink">
            <p className="font-semibold">No contact to email</p>
            <p className="mt-1 text-brown-400">
              This client has no contact with an email address. Add a contact to
              email the presentation — or share the link without emailing.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={!projectDetailQ.data}
                onClick={() => {
                  const clientId = projectDetailQ.data?.client.id;
                  if (clientId) navigateRef(`/clients/${clientId}/contacts`);
                }}
                title="Open this client's contacts to add an email address"
              >
                {projectDetailQ.data ? "Add contact" : "Loading client…"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={send.isPending}
                onClick={() => send.mutate({ publishLinkOnly: true })}
                title="Publish the share link without emailing anyone. The status will show 'Link shared — no email sent'."
              >
                Share link only
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setNoContactPrompt(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        {/* Preview and share (W10 primary flow): the client's link, copy, and
            the client view itself — where the PDF is saved. */}
        <section aria-labelledby="share-heading" className="rounded-md border border-rule bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 id="share-heading" className="text-lg font-semibold tracking-[-0.4px] text-ink">
                Preview and share
              </h2>
              <p className="mt-1 text-sm text-muted">
                {p.status === "draft"
                  ? "The client can't open this link until you send the presentation. You can preview it now."
                  : "The client's private link. Anyone with it can view the presentation and send notes."}
              </p>
            </div>
            {p.status === "draft" && (
              <span className="inline-flex items-center whitespace-nowrap rounded bg-warn-bg px-2 py-1 text-[11px] font-medium text-warn">
                Link not active yet
              </span>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <input
              readOnly
              value={shareUrl}
              aria-label="Client share link"
              onFocus={(e) => e.currentTarget.select()}
              className="h-10 min-w-0 flex-1 basis-64 rounded-md border border-rule bg-canvas px-3 font-mono text-xs text-muted"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => void copyShareLink()}
              title={copied ? "Link copied to clipboard" : "Copy the share link"}
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5" /> Copied!
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" /> Copy link
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => window.open(shareUrl, "_blank", "noopener,noreferrer")}
              title="Open the client's view in a new tab — save it as a PDF from there"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Preview &amp; Download
            </Button>
          </div>
        </section>

        {/* Send-to-client recipient picker (reused from estimate/SO, 2026-07-23).
            Multi-select client contacts + ad-hoc emails + "Send me a copy" + the
            "Add this person as a contact" popup. Needs the client id — resolved
            from the project detail (loaded when the modal opens). */}
        {showSendModal &&
          (projectDetailQ.isLoading ? (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40"
              role="status"
              aria-live="polite"
            >
              <div className="rounded-lg bg-white px-6 py-4 text-sm text-brown-400 shadow-xl">
                Loading client info…
              </div>
            </div>
          ) : projectDetailQ.data ? (
            <SendToCustomerModal
              title={`Send Presentation P-${p.presentationNumber}`}
              subtitle="Email the share link to the client and anyone you'd like to copy."
              clientId={projectDetailQ.data.client.id}
              ctaLabel={
                missingLinkConfirmPending
                  ? "Send anyway"
                  : isResend
                    ? "Resend"
                    : "Send"
              }
              // Armed soft gate → rust, so the second click can't be muscle
              // memory on an identical peach button (PO $0-decoration precedent).
              ctaVariant={missingLinkConfirmPending ? "destructive" : "primary"}
              showCopyToMe
              allowAddContact
              showBodyEditor
              composeUrl={`/presentations/${p.id}/send-compose`}
              bodyLabel="Email to the client"
              linkNoun="presentation link"
              currentUserEmail={user?.email ?? null}
              submitting={send.isPending}
              errorMessage={sendModalError}
              noticeMessage={sendModalNotice}
              onCancel={() => {
                if (send.isPending) return;
                setShowSendModal(false);
                setSendModalError(null);
                setSendModalNotice(null);
                setMissingLinkConfirmPending(false);
              }}
              onSubmit={(recipients, options) => {
                // Clear stale errors but KEEP the notice: while the armed second
                // submit is in flight it still explains why the CTA changed.
                setSendModalError(null);
                send.mutate({
                  recipients,
                  sendCopyToMe: options?.sendCopyToMe,
                  bcc: options?.bcc,
                  bodyHtml: options?.bodyHtml,
                  // Second click after the missing-link soft gate sends anyway.
                  skipMissingLinkCheck: missingLinkConfirmPending,
                });
              }}
            />
          ) : null)}

        {/* Clone target modal (PR #135) */}
        {showCloneModal &&
          (projectDetailQ.isLoading ? (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40"
              role="status"
              aria-live="polite"
            >
              <div className="rounded-lg bg-white px-6 py-4 text-sm text-brown-400 shadow-xl">
                Loading project info…
              </div>
            </div>
          ) : projectDetailQ.data ? (
            <CloneTargetModal
              title={`Copy Presentation P-${p.presentationNumber}`}
              defaultClient={projectDetailQ.data.client}
              defaultProjectName={`Copy of ${projectDetailQ.data.project.name}`}
              sourceItems={items.map<CloneSourceItem>((it) => ({
                id: it.id,
                label:
                  (it.productSnapshot as { name?: string } | null)?.name ??
                  "Untitled product",
              }))}
              submitting={clone.isPending}
              errorMessage={cloneError}
              onCancel={() => {
                if (clone.isPending) return;
                setShowCloneModal(false);
                setCloneError(null);
              }}
              onSubmit={(next) => clone.mutate(next)}
            />
          ) : null)}

        {/* Convert item-selection modal (PR #149, 2026-05-26) */}
        {convertTarget !== null && (
          <ConvertItemSelectionModal
            title={`Create a quote from P-${p.presentationNumber}`}
            ctaLabel="Create quote"
            sourceItems={convertSourceItems}
            preselectedItemIds={clientSelectedItemIds}
            submitting={convert.isPending}
            errorMessage={convertError}
            onCancel={() => {
              if (convert.isPending) return;
              setConvertTarget(null);
              setConvertError(null);
            }}
            notice={convertNotice}
            noticeAction={
              convertedOrderId
                ? {
                    label: "Open the document anyway",
                    onClick: () => {
                      setConvertTarget(null);
                      setConvertNotice(null);
                      navigateRef(`/orders/${convertedOrderId}`);
                    },
                  }
                : null
            }
            onSubmit={(next: ConvertSelectionResult) =>
              convert.mutate({
                target: convertTarget,
                selected_item_ids: next.selectedItemIds,
                item_quantities: next.itemQuantities,
              })
            }
          />
        )}
      </div>
    </div>
  );
}

// ─── Edit form ─────────────────────────────────────────────────────────

// The client page has two views (2026-10-07 other redesign): Rows and Grid.
// The four older layout values still save and still work — each maps to the
// view named in its label — but only Rows and Grid are offered for new picks.
const LAYOUT_OPTIONS: Array<{ value: PresentationLayout; label: string; legacy?: true }> = [
  { value: "list", label: "Rows — one product per line" },
  { value: "grid", label: "Grid — compact cards" },
  { value: "cards", label: "Cards (opens as grid)", legacy: true },
  { value: "spotlight", label: "Spotlight (opens as rows)", legacy: true },
  { value: "compact", label: "Compact (opens as grid)", legacy: true },
  { value: "minimal", label: "Minimal (opens as rows)", legacy: true },
];

function EditForm(props: {
  presentation: PresentationDetail;
  canEdit: boolean;
  onMutated: () => void;
}) {
  const { presentation: p, canEdit, onMutated } = props;
  const [introduction, setIntroduction] = useState(p.introduction ?? "");
  const [inHandsDate, setInHandsDate] = useState(p.inHandsDate ?? "");
  const [layout, setLayout] = useState<PresentationLayout>(
    (p.layout as PresentationLayout) ?? "list",
  );
  const [hidePricing, setHidePricing] = useState(p.hidePricing ?? false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setIntroduction(p.introduction ?? "");
    setInHandsDate(p.inHandsDate ?? "");
    setLayout((p.layout as PresentationLayout) ?? "list");
    setHidePricing(p.hidePricing ?? false);
  }, [
    p.id,
    p.introduction,
    p.inHandsDate,
    p.layout,
    p.hidePricing,
  ]);

  // 2026-05-26 — auto-save on blur (text) / change (select / checkbox /
  // date). Removed the explicit Save button per operator feedback:
  // items + prices already auto-save, so the dedicated Save button
  // for these 4 header fields was inconsistent. Reps would see it
  // greyed-out and think the page was auto-saving these too (it wasn't).
  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api(`/presentations/${p.id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      onMutated();
    },
  });

  const commitIntroduction = (): void => {
    const trimmed = introduction.trim();
    if (trimmed === (p.introduction ?? "")) return;
    save.mutate({ introduction: trimmed.length === 0 ? null : trimmed });
  };
  const commitInHandsDate = (value: string): void => {
    const next = value || null;
    if (next === (p.inHandsDate ?? null)) return;
    setInHandsDate(value);
    save.mutate({ in_hands_date: next });
  };
  const commitLayout = (value: PresentationLayout): void => {
    if (value === ((p.layout as PresentationLayout) ?? "list")) return;
    setLayout(value);
    save.mutate({ layout: value });
  };
  const commitHidePricing = (value: boolean): void => {
    if (value === (p.hidePricing ?? false)) return;
    setHidePricing(value);
    save.mutate({ hide_pricing: value });
  };

  // "Saved · just now" lingers for 2s so the rep sees confirmation,
  // then fades. While a save is in flight we show "Saving…".
  const status: string | null = save.isPending
    ? "Saving…"
    : savedAt && Date.now() - savedAt < 2000
      ? "Saved"
      : null;

  return (
    <section className="rounded-md border border-rule bg-white p-5">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
          Details
        </h2>
        {status && (
          <span className="text-xs text-muted" role="status">{status}</span>
        )}
      </div>
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1.5 block text-xs text-muted">
            Introduction
          </span>
          <textarea
            value={introduction}
            onChange={(e) => setIntroduction(e.target.value)}
            onBlur={commitIntroduction}
            rows={5}
            maxLength={10_000}
            disabled={!canEdit}
            placeholder="A short note to the client, shown at the top of their page. Leave blank for our standard welcome."
            className="w-full rounded-md border border-brown-100 bg-white px-3 py-2 text-sm text-brown-500 placeholder:text-[#8a8a85] focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200 disabled:bg-cream-50"
          />
        </label>
        <div className="grid gap-4 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1.5 block text-xs text-muted">
              In-hands date
            </span>
            <Input
              type="date"
              value={inHandsDate}
              onChange={(e) => commitInHandsDate(e.target.value)}
              disabled={!canEdit}
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs text-muted">
              Opens as
            </span>
            <select
              value={layout}
              onChange={(e) =>
                commitLayout(e.target.value as PresentationLayout)
              }
              disabled={!canEdit}
              className="h-10 w-full rounded-md border border-rule bg-white px-3 text-sm text-ink focus:border-ink focus:outline-none disabled:bg-canvas"
            >
              {LAYOUT_OPTIONS.filter((opt) => !opt.legacy || opt.value === layout).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-muted">
              More than 12 products opens as a grid. The client can switch.
            </span>
          </label>
          <label className="flex min-h-[40px] items-center gap-2 self-end">
            <input
              type="checkbox"
              checked={hidePricing}
              onChange={(e) => commitHidePricing(e.target.checked)}
              disabled={!canEdit}
              className="h-4 w-4 rounded border-brown-200 text-ink underline underline-offset-4 focus:ring-peach-300"
            />
            <span className="text-sm text-brown-500">Hide pricing from client</span>
          </label>
        </div>
        {error && <p className="text-sm text-rust-600">{error}</p>}
      </div>
    </section>
  );
}

// ─── Items ─────────────────────────────────────────────────────────────

function ItemsSection(props: {
  presentationId: string;
  items: PresentationItem[];
  canEdit: boolean;
  onMutated: () => void;
}) {
  const { presentationId, items, canEdit, onMutated } = props;
  const [adding, setAdding] = useState(false);
  // Floating "+ Add product" (Stephanie 2026-10-16) once the top one scrolls away.
  // Rep virtuals (2026-10-10): tick items → logo once → virtuals to review.
  const [virtualsOpen, setVirtualsOpen] = useState<"new" | "review" | null>(null);
  const latestVirtuals = usePresentationLatestSet(presentationId);
  const [error, setError] = useState<string | null>(null);
  // Peach, not rust. Confirmations and reassurances live here so they can never
  // be mistaken for failures — the same slot pattern HeaderSection already uses
  // for linkOnlyNotice / sentNotice.
  const [notice, setNotice] = useState<string | null>(null);

  // ─── Sections (2026-08-06) ────────────────────────────────────────────
  //
  // A section is a titled group. Ungrouped items render FIRST with no header,
  // which is exactly how every presentation built before this feature looks —
  // so nothing already sent to a client changes.
  //
  // `addTargetSectionId` is what makes the CEO's flow work: each section has
  // its own Add button, so a product lands under the heading the rep is
  // looking at rather than at the bottom of the list.
  const sectionsQuery = useQuery({
    queryKey: ["presentation-sections", presentationId],
    queryFn: async () =>
      api<{ sections: PresentationSection[] }>(
        `/presentations/${presentationId}/sections`,
      ),
  });
  const sections = sectionsQuery.data?.sections ?? [];
  const [addTargetSectionId, setAddTargetSectionId] = useState<string | null>(
    null,
  );
  // Which title input to focus once the new blank section renders. The whole
  // point of the flow is that you click + Section and start typing.
  const [focusSectionId, setFocusSectionId] = useState<string | null>(null);

  const refreshSections = (): void => {
    void sectionsQuery.refetch();
  };

  const createSection = useMutation({
    mutationFn: async () =>
      api<{ section: PresentationSection }>(
        `/presentations/${presentationId}/sections`,
        { method: "POST", body: JSON.stringify({}) },
      ),
    onError: (err) => setError(extractError(err)),
    onSuccess: (resp) => {
      setError(null);
      setFocusSectionId(resp.section.id);
      refreshSections();
    },
  });

  const renameSection = useMutation({
    mutationFn: async (args: { id: string; title: string }) =>
      api(`/presentation_sections/${args.id}`, {
        method: "PATCH",
        body: JSON.stringify({ title: args.title }),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      refreshSections();
    },
  });

  // ─── Drag and drop (2026-08-06) ──────────────────────────────────────
  //
  // CEO: commonsku reorders BOTH sections and products by dragging, and that
  // is what he uses every day. Native HTML5 drag, matching the existing house
  // pattern on the sales pipeline board — no new dependency.
  //
  // The up/down arrows STAY. They are not a competing model: both do exactly
  // the same thing now (reorder within a group), and the arrows are the only
  // keyboard-reachable path — native drag has none. Drag is the primary
  // interaction; the arrows are the accessible equivalent.
  const [draggingItemId, setDraggingItemId] = useState<string | null>(null);
  const [draggingSectionId, setDraggingSectionId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  /** Drop a product ONTO another product: take its slot, and its section.
   *  One gesture covers both reordering and moving between headings. */
  const dropItemOnItem = (targetItem: PresentationItem): void => {
    const dragged = items.find((i) => i.id === draggingItemId);
    setDraggingItemId(null);
    setDropTarget(null);
    if (!dragged || dragged.id === targetItem.id) return;
    if (dragged.sectionId !== targetItem.sectionId) {
      assignSection.mutate({
        itemId: dragged.id,
        sectionId: targetItem.sectionId,
      });
    }
    // 🔴 EQUAL POSITIONS (2026-08-11). Swapping two positions is a no-op when
    // they are the same, and until today EVERY item was inserted at 0 — all 46
    // items in production sat at position 0, so the grip did nothing on any
    // presentation. New items now get max+1 (services/presentations.ts), but
    // existing presentations still hold ties, so the swap has to break them.
    //
    // Same shape as dropSectionOnSection, which has handled this since it
    // shipped — the item path simply never got the equivalent.
    if (dragged.position === targetItem.position) {
      reorder.mutate({ itemId: dragged.id, position: targetItem.position + 1 });
      return;
    }
    reorder.mutate({ itemId: dragged.id, position: targetItem.position });
    reorder.mutate({ itemId: targetItem.id, position: dragged.position });
  };

  /** Drop a product onto a section's empty area: file it under that heading.
   *  This is what makes an empty section usable as a drop target. */
  const dropItemOnSection = (sectionId: string | null): void => {
    const dragged = items.find((i) => i.id === draggingItemId);
    setDraggingItemId(null);
    setDropTarget(null);
    if (!dragged || dragged.sectionId === sectionId) return;
    assignSection.mutate({ itemId: dragged.id, sectionId });
  };

  /** Drop a section onto another section: take its position. */
  const dropSectionOnSection = (targetSection: PresentationSection): void => {
    const draggedId = draggingSectionId;
    setDraggingSectionId(null);
    setDropTarget(null);
    if (draggedId === null || draggedId === targetSection.id) return;
    const dragged = sections.find((x) => x.id === draggedId);
    if (!dragged) return;
    if (dragged.position === targetSection.position) {
      reorderSection.mutate({
        id: dragged.id,
        position: targetSection.position + 1,
      });
      return;
    }
    reorderSection.mutate({ id: dragged.id, position: targetSection.position });
    reorderSection.mutate({ id: targetSection.id, position: dragged.position });
  };

  // Reorder sections. Same shape as the item arrows: swap positions with the
  // adjacent SIBLING, so what the arrow does is what the rep sees happen. The
  // client-facing view sorts sections by position, so this reorders the
  // client's page too — which is the whole point of being able to lead with
  // Apparel one week and Drinkware the next.
  const reorderSection = useMutation({
    mutationFn: async (args: { id: string; position: number }) =>
      api(`/presentation_sections/${args.id}`, {
        method: "PATCH",
        body: JSON.stringify({ position: args.position }),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      refreshSections();
    },
  });

  const moveSection = (idx: number, dir: -1 | 1): void => {
    const current = sections[idx];
    const target = sections[idx + dir];
    if (!current || !target) return;
    const a = current.position;
    const b = target.position;
    // Defensive, mirroring the item arrows: sections created back-to-back can
    // share a position, and a plain swap would then be a no-op.
    if (a === b) {
      reorderSection.mutate({
        id: current.id,
        position: dir === -1 ? b - 10 : b + 10,
      });
      return;
    }
    reorderSection.mutate({ id: current.id, position: b });
    reorderSection.mutate({ id: target.id, position: a });
  };

  const deleteSection = useMutation({
    mutationFn: async (id: string) =>
      api<{ itemsReleased: number }>(`/presentation_sections/${id}`, {
        method: "DELETE",
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: (resp) => {
      // Say what happened to the products. A rep who just clicked a bin icon
      // needs to know their items are safe, not guess.
      //
      // Through `notice`, NOT `error`. This reassurance used to render in
      // text-rust-600 — failure red — so the message "nothing was deleted"
      // arrived looking exactly like a failure, undercutting the one thing it
      // exists to say. The round-61 convention on this page is explicit that a
      // confirmation must never wear failure styling.
      setError(null);
      setNotice(
        resp.itemsReleased > 0
          ? `Section removed. ${resp.itemsReleased} product${resp.itemsReleased === 1 ? "" : "s"} moved out of it — nothing was deleted.`
          : null,
      );
      refreshSections();
      onMutated();
    },
  });

  const assignSection = useMutation({
    mutationFn: async (args: { itemId: string; sectionId: string | null }) =>
      api(`/presentation_items/${args.itemId}/section`, {
        method: "PATCH",
        body: JSON.stringify({ section_id: args.sectionId }),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
  });

  // Add-a-product handler for the shared <AddProductModal>. Kept ON THE PAGE
  // (not inside the shared modal) so the presentation retains its resolve →
  // POST /presentations/:id/items contract. A merch'd list-row Add hands its
  // row.id here; an ESP add hands the promoted INTERNAL merch'd id — both route
  // through the SAME path. Resolves the canonical merch'd product → preferred
  // supplier product id, POSTs the item, then refetches so the just-added card
  // greys out with "Added". Throws a friendly message on failure — the shared
  // modal shows the banner and leaves the row un-added.
  const addByMerchdId = async (merchdId: string): Promise<void> => {
    try {
      const resp = await api<{
        product: {
          offers: Array<{ isPreferred: boolean; supplierProductId: string }>;
        };
      }>(`/storefront/products/${merchdId}`);
      const preferred = resp.product.offers.find((o) => o.isPreferred);
      const supplierProductId =
        preferred?.supplierProductId ??
        resp.product.offers[0]?.supplierProductId;
      if (!supplierProductId) {
        throw new Error("This product has no supplier offers available.");
      }
      const created = await api<{ item: { id: string } }>(
        `/presentations/${presentationId}/items`,
        {
          method: "POST",
          body: JSON.stringify({ product_id: supplierProductId }),
        },
      );
      // Added from a section's own Add button → file it under that heading.
      // Best-effort: if this fails the product is still ON the presentation,
      // just ungrouped, which is recoverable and visible. Losing the product
      // would not be.
      if (addTargetSectionId !== null) {
        try {
          await api(`/presentation_items/${created.item.id}/section`, {
            method: "PATCH",
            body: JSON.stringify({ section_id: addTargetSectionId }),
          });
        } catch {
          setError(
            "Product added, but it could not be filed under that section. Move it with the section dropdown on the card.",
          );
        }
      }
      onMutated();
    } catch (err) {
      // Surface the friendly copy (e.g. the duplicate-item 409 → readable
      // sentence) through the shared modal's banner.
      throw new Error(extractError(err));
    }
  };

  const reorder = useMutation({
    mutationFn: async (args: { itemId: string; position: number }) =>
      api(`/presentation_items/${args.itemId}`, {
        method: "PATCH",
        body: JSON.stringify({ position: args.position }),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
  });

  const remove = useMutation({
    mutationFn: async (itemId: string) =>
      api(`/presentation_items/${itemId}`, { method: "DELETE" }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
  });

  // Swap an item with its neighbour INSIDE the same visible group, so what the
  // arrow does is always what the rep sees happen.
  const moveWithinGroup = (
    group: PresentationItem[],
    idx: number,
    dir: -1 | 1,
  ): void => {
    const current = group[idx];
    const target = group[idx + dir];
    if (!target || !current) return;
    const a = current.position;
    const b = target.position;
    // Defensive: newly-added items all default to position 0, so a plain swap
    // would be a no-op. Step clear of the neighbour instead.
    if (a === b) {
      reorder.mutate({ itemId: current.id, position: dir === -1 ? b - 10 : b + 10 });
      return;
    }
    reorder.mutate({ itemId: current.id, position: b });
    reorder.mutate({ itemId: target.id, position: a });
  };

  // Reorder WITHIN the visible group only.
  //
  // An earlier cut swapped positions with the item's neighbour in the GLOBAL
  // position-sorted array, on the theory that positions are presentation-wide
  // so the arrows should carry across a section boundary. That produced a DEAD
  // CLICK at the edge of every group: the swap fired, a PATCH went out, and
  // the visible order did not change — because grouping is by section first,
  // and swapping positions with an item in a different group reorders nothing
  // a rep can see. One dead click followed by a working one, with no error.
  //
  // The arrows now do exactly one job: order within a heading. Moving an item
  // BETWEEN sections is the section dropdown's job. One control, one meaning.
  // Grouping goes through the SHARED, gate-executed, total function rather than
  // a filter per section. See groupItemsForBuilder: the old hand-filtering had
  // no fallback bucket, so an item whose section was missing from the locally
  // cached `sections` list rendered NOWHERE on the rep's own screen.
  const builderGroups = groupItemsForBuilder(items, sections);
  const builderItemsFor = (key: string): PresentationItem[] =>
    builderGroups.find((g) => g.key === key)?.items ?? [];
  const unfiledItems = builderItemsFor("__unfiled");

  const renderGroup = (group: PresentationItem[]): React.ReactNode => (
    <ol className="space-y-3">
      {group.map((it, groupIdx) => {
        return (
          <ItemCard
            key={it.id}
            item={it}
            isFirst={groupIdx === 0}
            isLast={groupIdx === group.length - 1}
            groupIndex={groupIdx}
            groupSize={group.length}
            isDragging={draggingItemId === it.id}
            isDropTarget={dropTarget === `item:${it.id}`}
            onDragStart={() => setDraggingItemId(it.id)}
            onDragEnd={() => {
              setDraggingItemId(null);
              setDropTarget(null);
            }}
            onDragOverCard={() => {
              if (draggingItemId !== null && draggingItemId !== it.id) {
                setDropTarget(`item:${it.id}`);
              }
            }}
            onDropOnCard={() => dropItemOnItem(it)}
            canEdit={canEdit}
            reorderPending={reorder.isPending}
            removePending={remove.isPending}
            sections={sections}
            onAssignSection={(sectionId) =>
              assignSection.mutate({ itemId: it.id, sectionId })
            }
            siblingItems={items
              .filter((other) => other.id !== it.id)
              .map((other) => ({
                id: other.id,
                name:
                  (other.productSnapshot as { name?: string } | null)?.name ??
                  "Untitled product",
              }))}
            onMoveUp={() => moveWithinGroup(group, groupIdx, -1)}
            onMoveDown={() => moveWithinGroup(group, groupIdx, 1)}
            onRemove={() => remove.mutate(it.id)}
            onMutated={onMutated}
          />
        );
      })}
    </ol>
  );

  return (
    <PresentationVirtualsProvider presentationId={presentationId} open={() => setVirtualsOpen("review")}>
    {virtualsOpen && (
      <MakeVirtualsDialog
        presentationId={presentationId}
        initialSetId={virtualsOpen === "review" ? latestVirtuals.data?.latestSetId ?? null : null}
        onClose={() => {
          setVirtualsOpen(null);
          void latestVirtuals.refetch();
        }}
      />
    )}
    <section className="space-y-3">
      {/* Locked to the top while you scroll — no floating button
          (Stephanie 2026-10-27). */}
      <div className="sticky top-[68px] z-10 -mx-2 border-b border-rule bg-canvas px-2 py-2 flex items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
          Products{" "}
          <span className="text-sm font-normal text-muted tabular-nums">({items.length})</span>
        </h2>
        {canEdit && !adding && (
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <Button type="button" variant="outline" onClick={() => setVirtualsOpen("new")}>
                Make virtuals
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setError(null);
                createSection.mutate();
              }}
              disabled={createSection.isPending}
            >
              <Plus className="mr-1 h-4 w-4" /> Section
            </Button>
            <Button
              type="button"
              onClick={() => {
                setError(null);
                setAddTargetSectionId(null);
                setAdding(true);
              }}
            >
              <Plus className="mr-1 h-4 w-4" /> Add product
            </Button>
          </div>
        )}
      </div>

      {adding && (
        <AddProductModal
          existingProductIds={new Set(items.map((i) => i.productId))}
          onAdd={(row) => addByMerchdId(row.id)}
          customProductSlot={
            <CustomProductAffordance
              presentationId={presentationId}
              targetSectionId={addTargetSectionId}
              onItemAdded={onMutated}
            />
          }
          onClose={() => setAdding(false)}
        />
      )}

      {error && <p className="text-sm text-rust-600">{error}</p>}
      {notice && (
        <p className="rounded-lg border border-peach-300 bg-peach-100 px-4 py-3 text-sm text-brown-500">
          {notice}
        </p>
      )}

      {items.length === 0 && !adding && (
        <div className="rounded-lg border border-brown-50 bg-white px-5 py-10 text-center">
          <p className="text-sm text-brown-300">
            No products yet. Add a few from the catalog so the client has
            something to look at.
          </p>
        </div>
      )}

      {renderGroup(builderItemsFor("__ungrouped"))}

      {sections.map((section, sectionIdx) => {
        const sectionItems = builderItemsFor(section.id);
        return (
          <div
            key={section.id}
            onDragOver={(e) => {
              e.preventDefault();
              if (draggingSectionId !== null && draggingSectionId !== section.id) {
                setDropTarget(`section:${section.id}`);
              } else if (draggingItemId !== null) {
                setDropTarget(`section:${section.id}`);
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              // A dragged SECTION reorders; a dragged PRODUCT files under this
              // heading. Item drops on a specific card are handled by the card
              // itself and stop propagation, so this is the "dropped on the
              // section, not on a product" case — including an empty section.
              if (draggingSectionId !== null) dropSectionOnSection(section);
              else if (draggingItemId !== null) dropItemOnSection(section.id);
            }}
            className={cn(
              "space-y-3 rounded-lg pt-2 transition-colors",
              dropTarget === `section:${section.id}` &&
                "bg-rust-50/40 ring-1 ring-rust-200",
            )}
          >
            <SectionHeader
              section={section}
              itemCount={sectionItems.length}
              canEdit={canEdit}
              isFirst={sectionIdx === 0}
              isLast={sectionIdx === sections.length - 1}
              reorderPending={reorderSection.isPending}
              onMoveUp={() => moveSection(sectionIdx, -1)}
              onMoveDown={() => moveSection(sectionIdx, 1)}
              isDragging={draggingSectionId === section.id}
              onDragStart={() => setDraggingSectionId(section.id)}
              onDragEnd={() => {
                setDraggingSectionId(null);
                setDropTarget(null);
              }}
              autoFocus={focusSectionId === section.id}
              onFocused={() => setFocusSectionId(null)}
              onRename={(title) =>
                renameSection.mutate({ id: section.id, title })
              }
              onDelete={() => deleteSection.mutate(section.id)}
              onAddProduct={() => {
                setError(null);
                setAddTargetSectionId(section.id);
                setAdding(true);
              }}
            />
            {sectionItems.length === 0 ? (
              <p className="rounded-lg border border-dashed border-brown-100 bg-white px-4 py-6 text-center text-sm text-brown-300">
                Nothing here yet. Use Add product above to put something under
                this heading.
              </p>
            ) : (
              renderGroup(sectionItems)
            )}
          </div>
        );
      })}

      {/* The bucket that used to not exist. An item pointing at a section this
          page does not know about is SHOWN and named as needing attention,
          rather than silently rendering nowhere. Reachable if another tab
          deletes a section, or if the sections refetch lags or fails while the
          items one succeeds. The rep can re-file with the dropdown on the card
          itself, so this is recoverable in place. */}
      {unfiledItems.length > 0 && (
        <div className="space-y-3 pt-2">
          <div className="rounded-lg border border-rust-200 bg-rust-50/60 px-4 py-3">
            <p className="text-sm font-semibold text-brown-500">
              Unfiled {unfiledItems.length === 1 ? "product" : "products"}
            </p>
            <p className="mt-1 text-sm text-brown-400">
              {unfiledItems.length === 1 ? "This product" : "These products"} sat
              under a heading that no longer exists. Nothing has been lost —
              pick a section below, or refresh the page.
            </p>
          </div>
          {renderGroup(unfiledItems)}
        </div>
      )}
    </section>
    </PresentationVirtualsProvider>
  );
}

/** Move one item between sections. Extracted rather than inlined in the card's
 *  footer row so that row stays short and legible — and so qa-product-edit-modal's
 *  proximity assertion on the reorder controls keeps its meaning. Renders
 *  nothing at all until the presentation actually has a section. */
function SectionSelect(props: {
  sections: PresentationSection[];
  value: string | null;
  onChange: (sectionId: string | null) => void;
}) {
  const { sections, value, onChange } = props;
  if (sections.length === 0) return null;
  return (
    <select
      aria-label="Section"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
      className="mr-1 max-w-[9rem] truncate rounded-md border border-brown-100 bg-white px-1.5 py-1 text-xs text-brown-500"
    >
      <option value="">No section</option>
      {sections.map((sec) => (
        <option key={sec.id} value={sec.id}>
          {sec.title || "Untitled section"}
        </option>
      ))}
    </select>
  );
}

// ─── Section header ───────────────────────────────────────────────────
//
// The title is edited IN PLACE — a rep clicks + Section, gets a blank row with
// the cursor already in it, and types. Committed on blur or Enter rather than
// on every keystroke, so a long title isn't a PATCH per character.

function SectionHeader(props: {
  section: PresentationSection;
  itemCount: number;
  canEdit: boolean;
  isFirst: boolean;
  isLast: boolean;
  reorderPending: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isDragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  autoFocus: boolean;
  onFocused: () => void;
  onRename: (title: string) => void;
  onDelete: () => void;
  onAddProduct: () => void;
}) {
  const {
    section,
    itemCount,
    canEdit,
    isFirst,
    isLast,
    reorderPending,
    onMoveUp,
    onMoveDown,
    isDragging,
    onDragStart,
    onDragEnd,
    autoFocus,
    onFocused,
    onRename,
    onDelete,
    onAddProduct,
  } = props;
  const [draft, setDraft] = useState(section.title);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Re-sync when the server value changes underneath us (another tab, a
  // refetch) — but never while the rep is mid-edit in this input.
  useEffect(() => {
    if (document.activeElement !== inputRef.current) setDraft(section.title);
  }, [section.title]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      onFocused();
    }
  }, [autoFocus, onFocused]);

  const commit = (): void => {
    const next = draft.trim();
    if (next !== section.title) onRename(next);
  };

  return (
    <div
      className={cn(
        "flex items-center gap-2 border-b-2 border-peach-400 pb-1.5",
        isDragging && "opacity-50",
      )}
    >
      {canEdit && (
        // THE GRIP IS THE DRAG SOURCE. It always LOOKED like one — grip glyph,
        // cursor-grab — but `draggable` sat on the row while the title input
        // below carries `draggable={false}` (deliberately, so text selection
        // works) and `flex-1`, so the input covered nearly the whole row and
        // left only a sliver that actually started a drag. Reps reported
        // sections as unmovable while products dragged fine.
        //
        // ItemCard already solved this exact problem the right way; this is the
        // same shape. Do NOT move `draggable` back onto the row — that is what
        // broke it, and the input's `draggable={false}` is not the bug.
        //
        // The padding is not decoration either: it is what makes the handle a
        // real target rather than a 16px glyph.
        <div
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", section.id);
            onDragStart();
          }}
          onDragEnd={onDragEnd}
          // aria-hidden + title, matching ItemCard's grip. Native HTML5 drag has
          // never been keyboard-operable, so announcing this as a button would
          // promise a screen-reader user an action that does nothing. The
          // keyboard path is the up/down arrows beside it, which are real
          // buttons and always were.
          aria-hidden="true"
          title="Drag to reorder this category"
          // -my-1 + py-1 gives a full 24px of vertical hit area without changing
          // the row's height; px-1 makes it 24px wide and centred on the glyph
          // rather than a 20px box hanging off one side. Pointer targets below
          // 24x24 are the ones people miss — and this handle being hard to hit
          // is the entire defect being fixed.
          className="-my-1 flex flex-shrink-0 cursor-grab items-center px-1 py-1 text-muted hover:text-brown-400 active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" />
        </div>
      )}
      <input
        ref={inputRef}
        value={draft}
        draggable={false}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            inputRef.current?.blur();
          }
          if (e.key === "Escape") {
            setDraft(section.title);
            inputRef.current?.blur();
          }
        }}
        disabled={!canEdit}
        maxLength={120}
        placeholder="Section title"
        aria-label="Section title"
        className={cn(
          "min-w-0 flex-1 border-0 bg-transparent p-0 text-base font-semibold text-brown-500",
          // brown-300, not brown-200. brown-200 on white measures ~2.5:1, which
          // fails WCAG AA (4.5:1) — and this placeholder is the ONLY thing
          // telling a rep what the empty box is for, because the section is
          // created blank by design. brown-300 measures ~4.75:1. Both reviewers
          // raised it; the same fix already shipped on the estimate side.
          "placeholder:font-normal placeholder:text-[#8a8a85]",
          // A VISIBLE focus ring. This was `focus:outline-none focus:ring-0`,
          // which removes the browser outline AND suppresses the app's own ring,
          // so a keyboard user tabbing onto the title input saw nothing at all —
          // on the one control whose whole purpose is to be typed into. Matches
          // the ring every other input on this page uses; the offset keeps it
          // clear of the peach underline the header already draws.
          // NO focus:outline-none. The app's global :focus-visible rule
          // (index.css) is a 2px peach-400 outline at 2px offset, and it is what
          // DocumentSectionEditHeader — the SAME control on the estimate —
          // deliberately relies on. Substituting a local ring here gave one
          // control two different focus treatments across two documents a rep
          // moves between. The old `focus:ring-0` showed nothing at all; the fix
          // is to stop overriding, not to override differently.
        )}
      />
      <span className="flex-shrink-0 text-xs text-brown-300">
        {itemCount} {itemCount === 1 ? "product" : "products"}
      </span>
      {canEdit && !confirmingDelete && (
        <>
          <button
            type="button"
            aria-label="Move section up"
            // A visible tooltip, matching the grip and Remove in this same row.
            // The grip just gained one to stop it being a silent affordance;
            // leaving the arrows — the ONLY keyboard-operable reorder path —
            // without one would repeat that mistake beside the fix for it.
            title="Move this category up"
            onClick={onMoveUp}
            disabled={isFirst || reorderPending}
            className={cn(
              "flex-shrink-0 rounded-md p-1 text-brown-300 hover:bg-cream-100 hover:text-brown-500",
              "disabled:opacity-30 disabled:hover:bg-transparent",
            )}
          >
            <ArrowUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Move section down"
            title="Move this category down"
            onClick={onMoveDown}
            disabled={isLast || reorderPending}
            className={cn(
              "flex-shrink-0 rounded-md p-1 text-brown-300 hover:bg-cream-100 hover:text-brown-500",
              "disabled:opacity-30 disabled:hover:bg-transparent",
            )}
          >
            <ArrowDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onAddProduct}
            className="flex-shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-brown-400 hover:bg-cream-100 hover:text-brown-600"
          >
            <Plus className="mr-0.5 inline h-3.5 w-3.5" />
            Add product
          </button>
          <button
            type="button"
            aria-label="Remove section"
            title="Remove this heading. Products under it stay on the presentation."
            onClick={() => setConfirmingDelete(true)}
            // Divider + padding so the destructive control is not sitting 8px
            // from the positive one. Mis-clicking Add product and getting a
            // delete prompt is exactly the friction this buffer removes.
            className="ml-1 flex-shrink-0 rounded-md border-l border-brown-100 p-1 pl-2 text-brown-300 hover:bg-rust-50 hover:text-rust-500"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </>
      )}
      {canEdit && confirmingDelete && (
        // The app's own two-step pattern, not a browser dialog. A native
        // window.confirm on a REVERSIBLE action trains reps to click through
        // confirms, which blunts the ones that genuinely matter. It says what
        // happens to the products because that is the only real question.
        <>
          <span className="flex-shrink-0 text-xs text-brown-400">
            {itemCount > 0
              ? `Remove heading? ${itemCount} product${itemCount === 1 ? "" : "s"} stay.`
              : "Remove heading?"}
          </span>
          <button
            type="button"
            onClick={() => {
              setConfirmingDelete(false);
              onDelete();
            }}
            className="flex-shrink-0 rounded-md bg-ink px-2 py-1 text-xs font-semibold text-white hover:bg-ink-700"
          >
            Remove
          </button>
          <button
            type="button"
            onClick={() => setConfirmingDelete(false)}
            className="flex-shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-brown-400 hover:bg-cream-100"
          >
            Cancel
          </button>
        </>
      )}
    </div>
  );
}

// ─── Item card with tier table ────────────────────────────────────────

function ItemCard(props: {
  item: PresentationItem;
  isFirst: boolean;
  isLast: boolean;
  canEdit: boolean;
  reorderPending: boolean;
  removePending: boolean;
  siblingItems: Array<{ id: string; name: string }>;
  /** 0-based index within the item's VISIBLE group, and that group's size —
   *  for the human-readable "2 of 5" label. */
  groupIndex: number;
  groupSize: number;
  sections: PresentationSection[];
  onAssignSection: (sectionId: string | null) => void;
  isDragging: boolean;
  isDropTarget: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOverCard: () => void;
  onDropOnCard: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  onMutated: () => void;
}) {
  const {
    item: it,
    sections,
    onAssignSection,
    groupIndex,
    groupSize,
    isDragging,
    isDropTarget,
    onDragStart,
    onDragEnd,
    onDragOverCard,
    onDropOnCard,
    isFirst,
    isLast,
    siblingItems,
    canEdit,
    reorderPending,
    removePending,
    onMoveUp,
    onMoveDown,
    onRemove,
    onMutated,
  } = props;

  // A 1-based ordinal WITHIN this item's visible group. The raw `position`
  // column is not contiguous — new items default to 0 and the swap logic steps
  // by 10 — so showing it made a rep read "position 3" then "position 7" two
  // cards later as evidence something was broken.
  const ordinalLabel = `${groupIndex + 1} of ${groupSize}`;

  const snapshot = it.productSnapshot ?? {};
  const imageUrls = snapshot.imageUrls;
  const imageUrl = Array.isArray(imageUrls)
    ? imageUrls[0]
    : imageUrls && typeof imageUrls === "object"
      ? (Object.values(imageUrls)[0] as string | undefined)
      : undefined;
  const name = snapshot.name ?? "Untitled product";

  // 2026-05-26 (PR #152) — operator-locked commonsku-style edit flow:
  // the entire card is a click target that opens ProductEditModal. All
  // inline editors (description / tier table / charges / imprint) now
  // live inside the modal. Reorder + Remove stay outside the click
  // target so they don't trigger an unwanted modal open.
  const [editorOpen, setEditorOpen] = useState(false);

  const effectiveDescription =
    it.descriptionOverride ?? snapshot.description ?? "";
  const tierRange = (() => {
    if (it.tiers.length === 0) return null;
    const sells = it.tiers
      .map((t) => Number(t.sellPrice))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (sells.length === 0) return null;
    const lo = Math.min(...sells);
    const hi = Math.max(...sells);
    if (lo === hi) return `$${lo.toFixed(2)}`;
    return `$${lo.toFixed(2)}–$${hi.toFixed(2)}`;
  })();
  const vendorLabel = it.vendorName?.trim() || null;
  // 🔴 WHAT TELLS TWO COPIES OF THE SAME PRODUCT APART (2026-08-11).
  //
  // The same product may now appear twice on one presentation — that is the
  // whole point of the feature (one item, two logos). But vendor and SKU are
  // identical by definition on the two copies, so the meta line above described
  // them identically and a rep could not tell which was which.
  //
  // The distinguishing fact is the DECORATION, and it is already rendered this
  // exact way to the client on /p/:uuid ("Front · Full colour logo") and to the
  // vendor on the PO. Reusing that shape rather than inventing a label keeps the
  // three surfaces saying the same thing about the same item.
  const decoLabel = (() => {
    const d = (it.decorations ?? [])[0];
    if (!d) return null;
    const parts = [d.location?.trim() || null, d.logoName?.trim() || null].filter(
      Boolean,
    );
    if (parts.length === 0) return null;
    const extra = (it.decorations?.length ?? 0) - 1;
    return extra > 0 ? `${parts.join(" · ")} +${extra}` : parts.join(" · ");
  })();
  const metaLine = [
    snapshot.sku ? `SKU ${snapshot.sku}` : null,
    vendorLabel,
    decoLabel,
    ordinalLabel,
  ]
    .filter(Boolean)
    .join(" · ");
  const decorationCount = it.decorations?.length ?? 0;
  const chargeCount = it.itemCharges?.length ?? 0;

  return (
    <li
      // DROP target only. The drag SOURCE is the grip below, not the whole card.
      // Previously `draggable` sat here, which had two costs: there was no visual
      // cue that a product could be dragged at all (sections have had a grip
      // since day one), and the draggable area swallowed the card's own click
      // target and its footer controls. SectionHeader's comment already said
      // "draggable from the header, NOT the input" — this applies the same idea
      // to items, and actually enforces it rather than relying on the browser
      // preferring a text field over an ancestor's drag.
      onDragOver={(e) => {
        e.preventDefault();
        onDragOverCard();
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDropOnCard();
      }}
      className={cn(
        "flex items-stretch rounded-lg border bg-white transition-colors",
        isDragging && "opacity-50",
        isDropTarget ? "border-rust-300 bg-rust-50/40" : "border-brown-100",
      )}
    >
      {canEdit && (
        <div
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", it.id);
            onDragStart();
          }}
          onDragEnd={onDragEnd}
          // aria-hidden, matching SectionHeader's grip. An earlier cut gave this
          // role="button" + aria-label, which a screen reader can reach via its
          // rotor even at tabIndex={-1} — announcing an actionable button that
          // does nothing when activated, because native HTML5 drag has never
          // been keyboard-operable. The keyboard path is the up/down arrows and
          // the section dropdown, which are real buttons and always were.
          // A visible title is still useful for a mouse user.
          aria-hidden="true"
          title="Drag to reorder, or drop on a heading to move it there"
          className="flex flex-shrink-0 cursor-grab items-center pl-2 text-muted hover:text-brown-400 active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </div>
      )}
      <div className="min-w-0 flex-1">
      <button
        type="button"
        onClick={() => canEdit && setEditorOpen(true)}
        disabled={!canEdit}
        className={cn(
          "group flex w-full items-center gap-3 p-3 text-left",
          canEdit && "rounded-lg hover:bg-cream-50/60 focus:bg-cream-50/60",
        )}
        title={canEdit ? "Click to edit description, pricing, charges, and imprint" : undefined}
      >
        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-brown-50 bg-cream-100">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10px] uppercase tracking-wide text-brown-300">
              No image
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="line-clamp-1 font-semibold text-brown-500">{name}</p>
          {/* SKU · Supplier · ordinal. GM Stephanie 2026-08-11: "We should be
              able to see the vendor of each item along with the sku." It tells
              two otherwise-identical rows apart, now that the same product may
              appear twice on one presentation.
              🔒 ORDER AND COLOUR COPIED FROM DocumentLineCard's identical meta
              line (SKU then supplier, both rust-500) rather than invented here.
              A rep moves between the presentation card and the document card
              constantly; the same three facts rendered in a different order and
              a different colour on the two surfaces is a defect, and demoting
              the vendor to the palette's faintest tone would bury the very
              thing she asked to see. `truncate` because a custom-product
              supplier is free text a rep types, and a long one would wrap this
              line and make the card taller than its neighbours. */}
          <p className="mt-0.5 truncate text-xs" title={metaLine}>
            {snapshot.sku && (
              <span className="font-medium text-ink">{`SKU ${snapshot.sku}`}</span>
            )}
            {snapshot.sku && vendorLabel && (
              <span className="text-muted"> · </span>
            )}
            {vendorLabel && (
              <span className="font-medium text-ink">{vendorLabel}</span>
            )}
            {(snapshot.sku || vendorLabel) && (
              <span className="text-muted"> · </span>
            )}
            {decoLabel && (
              <>
                <span className="font-semibold text-brown-400">{decoLabel}</span>
                <span className="text-muted"> · </span>
              </>
            )}
            <span className="text-brown-300">{ordinalLabel}</span>
          </p>
          {effectiveDescription && (
            <p className="mt-1 line-clamp-2 text-xs text-brown-400">
              {effectiveDescription}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
            <Pill tone={tierRange ? "active" : "muted"}>
              {tierRange ? `${tierRange} · ${it.tiers.length} tier${it.tiers.length === 1 ? "" : "s"}` : "No pricing"}
            </Pill>
            <Pill tone={chargeCount > 0 ? "active" : "muted"}>
              {chargeCount > 0
                ? `${chargeCount} charge${chargeCount === 1 ? "" : "s"}`
                : "No charges"}
            </Pill>
            <Pill tone={decorationCount > 0 ? "active" : "muted"}>
              {decorationCount > 0
                ? `${decorationCount} imprint${decorationCount === 1 ? "" : "s"}`
                : "No imprint"}
            </Pill>
            {canEdit && (
              <span className="ml-auto rounded-md border border-brown-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brown-400 group-hover:border-ink group-hover:text-ink">
                Edit
              </span>
            )}
          </div>
        </div>
      </button>
      <ItemVirtualsStrip itemId={it.id} />
      {canEdit && (
        <div className="flex items-center justify-end gap-1 border-t border-brown-50 px-3 py-1.5">
          <SectionSelect
            sections={sections}
            value={it.sectionId}
            onChange={onAssignSection}
          />
          <button
            type="button"
            aria-label="Move up"
            onClick={onMoveUp}
            disabled={isFirst || reorderPending}
            className={cn(
              "rounded-md p-1.5 text-brown-300 hover:bg-cream-100 hover:text-brown-500",
              "disabled:opacity-30 disabled:hover:bg-transparent",
            )}
          >
            <ArrowUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Move down"
            onClick={onMoveDown}
            disabled={isLast || reorderPending}
            className={cn(
              "rounded-md p-1.5 text-brown-300 hover:bg-cream-100 hover:text-brown-500",
              "disabled:opacity-30 disabled:hover:bg-transparent",
            )}
          >
            <ArrowDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Remove"
            onClick={onRemove}
            disabled={removePending}
            className="rounded-md p-1.5 text-brown-300 hover:bg-rust-50 hover:text-rust-500 disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
      </div>
      {editorOpen && (
        <ProductEditModal
          item={it}
          siblingItems={siblingItems}
          canEdit={canEdit}
          onMutated={onMutated}
          onClose={() => setEditorOpen(false)}
        />
      )}
    </li>
  );
}

function Pill(props: {
  tone: "active" | "muted";
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 font-semibold uppercase tracking-wide",
        props.tone === "active"
          ? "bg-peach-100 text-brown-500"
          : "border border-brown-100 text-brown-300",
      )}
    >
      {props.children}
    </span>
  );
}

/** Convert starts each item at the SMALLEST quantity the client was shown —
 *  the top break overstated the order (pilot 2026-10-13). The rep changes it
 *  in the convert dialog to what the client actually asked for. */
function firstTierQty(tiers: PresentationItemTier[]): number {
  const shown = tiers.filter((t) => !t.isHiddenFromClient && t.quantity > 0);
  const pool = shown.length > 0 ? shown : tiers.filter((t) => t.quantity > 0);
  return pool.length > 0 ? Math.min(...pool.map((t) => t.quantity)) : 1;
}

// ─── Tier table ───────────────────────────────────────────────────────


/** The pricing grid, transposed: one COLUMN per quantity break.
 *
 *  🔴 CEO 2026-08-18: "allow editable columns". The previous grid put each tier
 *  on its own ROW behind an Edit button, so changing four costs was four
 *  open-edit-save cycles. Quantity breaks are what a rep thinks in, and they
 *  read across, so they are columns here and every cell is live.
 *
 *  MARGIN IS NOT EDITABLE and that is deliberate: `margin_pct` is a GENERATED
 *  column in Postgres, derived from cost and sell. Offering a box that looks
 *  editable and silently does nothing is worse than showing the number.
 *
 *  The "Auto" marker stays. A rep must be able to tell a supplier-derived price
 *  from one a human set before it goes to a client. */
function TierColumnGrid(props: {
  tiers: PresentationItemTier[];
  canEdit: boolean;
  onMutated: () => void;
  onError: (msg: string | null) => void;
  /** Ask the parent to open its confirm for pushing this column's cost across
   *  the others. The confirm itself stays on the parent because it is the same
   *  copy the old row layout used and it states what MOVES and what does not —
   *  the sell prices staying put is the part a rep relies on. */
  onCopyCost: (tierId: string) => void;
  /** How many columns that copy would actually rewrite. A button that provably
   *  does nothing is worse than no button, so a 0 hides it. */
  copyPlanLength: (tierId: string) => number;
}) {
  const { tiers, canEdit, onMutated, onError, onCopyCost, copyPlanLength } = props;
  const [pending, setPending] = useState<string | null>(null);

  const patch = useMutation({
    mutationFn: async (args: { id: string; body: Record<string, unknown> }) =>
      api(`/presentation_item_tiers/${args.id}`, {
        method: "PATCH",
        body: JSON.stringify(args.body),
      }),
    onMutate: (args) => { setPending(args.id); onError(null); },
    onError: (err) => { setPending(null); onError(extractError(err)); },
    onSuccess: () => { setPending(null); onMutated(); },
  });

  const del = useMutation({
    mutationFn: async (id: string) =>
      api(`/presentation_item_tiers/${id}`, { method: "DELETE" }),
    onError: (err) => onError(extractError(err)),
    onSuccess: () => onMutated(),
  });

  // Visibility is a PATCH like any other, but deliberately NOT routed through
  // TierCell: it is a toggle, not a typed value, and it must not flip
  // `is_custom` — see the note on the tier PATCH handler.
  const hide = useMutation({
    mutationFn: async (args: { tierId: string; hidden: boolean }) =>
      api(`/presentation_item_tiers/${args.tierId}`, {
        method: "PATCH",
        body: JSON.stringify({ is_hidden_from_client: args.hidden }),
      }),
    onError: (err) => onError(extractError(err)),
    onSuccess: () => onMutated(),
  });

  const cell = (t: PresentationItemTier) => ({
    saving: pending === t.id,
  });

  return (
    <div className="overflow-x-auto">
      <table className="text-xs">
        <tbody>
          <tr>
            <th scope="row" className="py-1.5 pr-3 text-left text-[10px] font-semibold uppercase tracking-wide text-brown-300">
              Quantity
            </th>
            {tiers.map((t) => (
              <td key={t.id} className="w-28 py-1.5 pr-2 align-middle">
                <LiveCell
                  value={String(t.quantity)}
                  ariaLabel={`Quantity for the ${t.quantity} break`}
                  disabled={!canEdit}
                  validate={(raw) => {
                    const q = Number.parseInt(raw, 10);
                    return Number.isInteger(q) && q > 0
                      ? null
                      : "Quantity must be a positive whole number.";
                  }}
                  onCommit={(raw) =>
                    patch.mutate({ id: t.id, body: { quantity: Number.parseInt(raw, 10) } })
                  }
                />
              </td>
            ))}
          </tr>
          <tr>
            <th scope="row" className="py-1.5 pr-3 text-left text-[10px] font-semibold uppercase tracking-wide text-brown-300">
              Cost
            </th>
            {tiers.map((t) => (
              <td key={t.id} className="py-1.5 pr-2 align-middle">
                <LiveCell
                  value={t.wholesaleCost}
                  ariaLabel={`Cost at ${t.quantity}`}
                  disabled={!canEdit}
                  validate={(raw) =>
                    isValidCostInput(raw) ? null : "Cost must be numeric (e.g. 4.25)."
                  }
                  onCommit={(raw) =>
                    patch.mutate({ id: t.id, body: { wholesale_cost: raw } })
                  }
                />
              </td>
            ))}
          </tr>
          <tr>
            <th scope="row" className="py-1.5 pr-3 text-left text-[10px] font-semibold uppercase tracking-wide text-brown-300">
              Sell
            </th>
            {tiers.map((t) => (
              <td key={t.id} className="py-1.5 pr-2 align-middle">
                <LiveCell
                  value={t.sellPrice}
                  ariaLabel={`Sell price at ${t.quantity}`}
                  disabled={!canEdit}
                  validate={(raw) =>
                    /^\d+(\.\d{1,2})?$/.test(raw) ? null : "Sell must be numeric (e.g. 9.95)."
                  }
                  onCommit={(raw) =>
                    patch.mutate({ id: t.id, body: { sell_price: raw } })
                  }
                />
              </td>
            ))}
          </tr>
          <tr>
            <th scope="row" className="py-1.5 pr-3 text-left text-[10px] font-semibold uppercase tracking-wide text-brown-300">
              Margin
            </th>
            {tiers.map((t) => (
              <td key={t.id} className="py-1.5 pr-2 text-right align-middle tabular-nums text-brown-400">
                {t.marginPct ? `${Number(t.marginPct).toFixed(1)}%` : "—"}
                {cell(t).saving && (
                  <span className="ml-1 text-[9px] uppercase text-muted">saving</span>
                )}
              </td>
            ))}
          </tr>
          <tr>
            <th scope="row" className="py-1.5 pr-3 text-left text-[10px] font-semibold uppercase tracking-wide text-brown-300">
              Source
            </th>
            {tiers.map((t) => (
              <td key={t.id} className="py-1.5 pr-2 text-right align-middle">
                <span
                  title={
                    t.isCustom
                      ? "Set by a rep on this presentation"
                      : "Auto-filled from the supplier's published price"
                  }
                  className="rounded-full bg-cream-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-brown-300"
                >
                  {t.isCustom ? "Rep" : "Supplier"}
                </span>
              </td>
            ))}
          </tr>
          <tr>
            <th scope="row" className="py-1.5 pr-3 text-left text-[10px] font-semibold uppercase tracking-wide text-brown-300">
              Client sees
            </th>
            {tiers.map((t) => (
              <td key={t.id} className="py-1.5 pr-2 text-right align-middle">
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() =>
                      hide.mutate({ tierId: t.id, hidden: !t.isHiddenFromClient })
                    }
                    disabled={hide.isPending}
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                      t.isHiddenFromClient
                        ? "border border-rust-300 text-rust-500 hover:bg-rust-50"
                        : "bg-peach-100 text-brown-500 hover:bg-peach-200",
                    )}
                  >
                    {t.isHiddenFromClient ? "Internal only" : "Visible"}
                  </button>
                ) : (
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                      t.isHiddenFromClient
                        ? "border border-rust-300 text-rust-500"
                        : "bg-peach-100 text-brown-500",
                    )}
                  >
                    {t.isHiddenFromClient ? "Internal only" : "Visible"}
                  </span>
                )}
              </td>
            ))}
          </tr>
          {canEdit && tiers.length > 1 && (
            <tr>
              <th scope="row" className="py-1.5 pr-3 text-left text-[10px] font-semibold uppercase tracking-wide text-brown-300">
                Copy cost
              </th>
              {tiers.map((t) => (
                <td key={t.id} className="py-1.5 pr-2 text-right align-middle">
                  {copyPlanLength(t.id) > 0 && (
                    <button
                      type="button"
                      onClick={() => onCopyCost(t.id)}
                      title="Set every other column to this cost. Sell prices do not move."
                      className="rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brown-300 hover:bg-cream-100 hover:text-brown-500"
                    >
                      Across →
                    </button>
                  )}
                </td>
              ))}
            </tr>
          )}
          {canEdit && (
            <tr>
              <th scope="row" className="py-1.5 pr-3 text-left text-[10px] font-semibold uppercase tracking-wide text-brown-300">
                Remove
              </th>
              {tiers.map((t) => (
                <td key={t.id} className="py-1.5 pr-2 text-right align-middle">
                  <button
                    type="button"
                    onClick={() => del.mutate(t.id)}
                    disabled={del.isPending}
                    aria-label={`Remove the ${t.quantity} break`}
                    className="rounded-md p-1 text-brown-300 hover:bg-cream-100 hover:text-rust-500 disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function TierTable(props: {
  itemId: string;
  tiers: PresentationItemTier[];
  canEdit: boolean;
  onMutated: () => void;
}) {
  const { itemId, tiers, canEdit, onMutated } = props;
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A product with NO supplier pricing on file gets the empty-state
  // "Wholesale $___" form below, which builds a curve from that one number.
  // That is the ONLY manual pricing entry point. There is deliberately no
  // control that rebuilds over pricing the supplier DID send.
  const [manualWholesale, setManualWholesale] = useState("");
  // Tier whose cost is about to be pushed across the grid, pending confirm.
  // Confirmed rather than one-click because it rewrites cost on rows the rep
  // is not looking at, and margins visibly move as a result.
  const [copySource, setCopySource] = useState<string | null>(null);

  const autoFill = useMutation({
    mutationFn: async (wholesaleCost: number | null) =>
      api(`/presentation_items/${itemId}/auto-fill-tiers`, {
        method: "POST",
        body: wholesaleCost === null
          ? "{}"
          : JSON.stringify({ wholesale_cost: wholesaleCost }),
      }),
    onSuccess: () => {
      setError(null);
      setManualWholesale("");
      onMutated();
    },
    onError: (err) => {
      // 422 = no canonical price; 409 = already has tiers. Surface a
      // friendlier message so the rep knows whether to manually set or
      // delete-then-retry.
      if (err instanceof ApiError) {
        if (err.status === 422) {
          setError(
            "No supplier pricing on file for this product. Set a wholesale price below and we'll build the 3-tier curve from it.",
          );
          return;
        }
        if (err.status === 409) {
          setError(
            "This item already has tiers. Delete them first if you want to re-run auto-fill.",
          );
          return;
        }
      }
      setError(extractError(err));
    },
  });

  const remove = useMutation({
    mutationFn: async (tierId: string) =>
      api(`/presentation_item_tiers/${tierId}`, { method: "DELETE" }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
  });

  const toggleHidden = useMutation({
    mutationFn: async (args: { tierId: string; hidden: boolean }) =>
      api(`/presentation_item_tiers/${args.tierId}`, {
        method: "PATCH",
        body: JSON.stringify({ is_hidden_from_client: args.hidden }),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
  });

  /** "Copy the net price to the other tiers if we have EQP" (GM, 2026-08-11).
   *
   *  🔴 COST ONLY. `margin_pct` is a generated column, so the margins re-derive
   *  and the client-facing SELL prices stay exactly where the rep put them.
   *  That is deliberate and it is the whole point: a sell price may already
   *  have been sent to a client, and this is the automation of typing the same
   *  net cost into five boxes — not a repricing.
   *
   *  The old "Override cost" control sat beside this and did the opposite: it
   *  DELETED every tier and rebuilt a 40% margin curve on a GENERIC quantity
   *  ladder, discarding the supplier's own costs AND its own breaks. Removed
   *  2026-08-18 on the CEO's word — "suppliers price wins. Reps can edit the
   *  price within the product anyway." It was measured overwriting correct
   *  supplier pricing on a live client presentation (PSRG17) leaving no audit
   *  row at all. Editing a single tier in the grid is the supported way to
   *  change a price; nothing rebuilds a curve over supplier data any more. */
  const copyCost = useMutation({
    mutationFn: async (sourceTierId: string) => {
      const writes = copyCostWrites({
        tiers: tiers.map((t) => ({ id: t.id, wholesaleCost: t.wholesaleCost })),
        sourceTierId,
      });
      for (const w of writes) {
        await api(`/presentation_item_tiers/${w.tierId}`, {
          method: "PATCH",
          body: JSON.stringify({ wholesale_cost: w.wholesale_cost }),
        });
      }
      return writes.length;
    },
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      setCopySource(null);
      onMutated();
    },
  });

  const sorted = [...tiers].sort(
    (a, b) => a.position - b.position || a.quantity - b.quantity,
  );

  /** Rows the pending copy would actually rewrite — drives both the confirm
   *  copy and whether the per-row button is worth offering at all. */
  const copyPlan = (tierId: string) =>
    copyCostWrites({
      tiers: tiers.map((t) => ({ id: t.id, wholesaleCost: t.wholesaleCost })),
      sourceTierId: tierId,
    });

  return (
    <div className="border-t border-brown-50 bg-cream-50/40 px-3 py-3">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
          Pricing tiers
        </p>
        <div className="flex items-baseline gap-3">
          {canEdit && !adding && (
            <button
              type="button"
              onClick={() => {
                setError(null);
                setAdding(true);
              }}
              className="text-xs font-semibold text-brown-400 underline hover:text-brown-500"
            >
              Add tier
            </button>
          )}
        </div>
      </div>
      {/* EQP copy confirm. States what MOVES and what does NOT — the sell
          prices staying put is the part a rep must be able to rely on. */}
      {canEdit && copySource !== null && (() => {
        const src = tiers.find((t) => t.id === copySource);
        const writes = copyPlan(copySource);
        if (!src) return null;
        return (
          <div className="mb-3 rounded-md border border-peach-200 bg-peach-50/40 p-2">
            <p className="text-xs text-brown-500">
              Set wholesale to{" "}
              <span className="font-semibold">
                {formatMills(toMills(src.wholesaleCost))}
              </span>{" "}
              on {writes.length} other tier{writes.length === 1 ? "" : "s"}?
            </p>
            <p className="mt-0.5 text-[10px] text-brown-300">
              Sell prices stay exactly as they are — margins will change to
              match the new cost.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => copyCost.mutate(copySource)}
                disabled={copyCost.isPending}
                className="rounded-md bg-peach-400 px-2.5 py-1 text-xs font-semibold text-white hover:bg-peach-500 disabled:opacity-50"
              >
                {copyCost.isPending ? "Applying…" : "Apply to all tiers"}
              </button>
              <button
                type="button"
                onClick={() => setCopySource(null)}
                disabled={copyCost.isPending}
                className="text-xs font-semibold text-brown-300 hover:text-brown-500"
              >
                Cancel
              </button>
            </div>
          </div>
        );
      })()}
      {error && (
        <p className="mb-2 text-xs text-rust-600">{error}</p>
      )}
      {sorted.length === 0 && !adding && (
        <div>
          <p className="mb-2 text-xs text-brown-300">
            No tiers yet — supplier pricing wasn't on file at add-time. Type
            the wholesale cost below and we'll build the EQP-curve tiers
            (40% margin at top qty, +5% per smaller bracket).
          </p>
          {canEdit && (
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const n = Number(manualWholesale);
                if (!Number.isFinite(n) || n <= 0) {
                  setError("Enter a positive wholesale price.");
                  return;
                }
                setError(null);
                autoFill.mutate(n);
              }}
            >
              <label className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
                Wholesale ($/unit)
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={manualWholesale}
                onChange={(e) => setManualWholesale(e.target.value)}
                placeholder="0.00"
                autoFocus
                className="w-24 rounded-md border border-brown-100 bg-white px-2 py-1 text-xs text-brown-500 focus:border-peach-400 focus:outline-none"
              />
              <button
                type="submit"
                disabled={autoFill.isPending || manualWholesale.trim() === ""}
                className="rounded-md bg-peach-400 px-2.5 py-1 text-xs font-semibold text-white hover:bg-peach-500 disabled:opacity-50"
              >
                {autoFill.isPending ? "Generating…" : "Build EQP-curve tiers"}
              </button>
            </form>
          )}
        </div>
      )}
      {sorted.length > 0 && (
        <TierColumnGrid
          tiers={sorted}
          canEdit={canEdit}
          onMutated={onMutated}
          onError={setError}
          onCopyCost={(tierId) => {
            setError(null);
            setCopySource(tierId);
          }}
          copyPlanLength={(tierId) => copyPlan(tierId).length}
        />
      )}
      {adding && (
        <TierBatchAddForm
          itemId={itemId}
          existingQuantities={sorted.map((t) => t.quantity)}
          onCancel={() => setAdding(false)}
          // Refresh the table whenever ANY row landed, including a partial run.
          onAdded={onMutated}
          // Only a clean run closes the form. Closing on a partial write would
          // hide the rows that never landed behind a table that looks right.
          onComplete={() => setAdding(false)}
        />
      )}
    </div>
  );
}

/** A draft row carries a stable `key` so removing a middle row doesn't make
 *  React re-key every input below it (which steals focus mid-typing). It
 *  structurally satisfies TierDraft, so it goes to planTierBatch unchanged. */
interface DraftRow extends TierDraft {
  key: string;
}

/** A price grid is almost never one row — the app's own auto-fill builds three.
 *  Opening on three is what turns "add a tier, five times" into one pass. */
const INITIAL_DRAFT_ROWS = 3;

/** Shared by the header row and every draft row so the columns line up. The
 *  two trailing `auto` tracks are the Internal checkbox and the remove button. */
const TIER_DRAFT_GRID =
  "grid grid-cols-[repeat(3,minmax(0,1fr))_auto_auto] gap-2";

function TierDraftInput(props: {
  /** Screen-reader name — the visible column headers sit in their own row, so
   *  each input still needs its own accessible label. */
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  invalid?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <Input
      type="text"
      inputMode="decimal"
      aria-label={props.label}
      aria-invalid={props.invalid ? true : undefined}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      placeholder={props.placeholder}
      autoFocus={props.autoFocus}
      className={cn(
        "h-9 px-2 text-xs",
        props.invalid && "border-rust-400 focus:border-rust-500",
      )}
    />
  );
}

/**
 * Add MANY tiers in one pass (GM, 2026-08-11: *"should be able to add as many
 * as we want at once — costing a lot of time"*). This replaced a single-row
 * form that closed on submit, so building a five-break grid meant five
 * open-type-submit cycles.
 *
 * ⚠️ THE API HAS NO BULK TIER ENDPOINT. This posts N times, which means a
 * mid-flight failure can leave a PARTIAL price grid on a live quote. Two things
 * hold that line, and both matter:
 *
 *  1. `planTierBatch` validates the WHOLE batch — including duplicate
 *     quantities, which the server answers 409 — before a single request goes
 *     out. That keeps the reachable failures off the network.
 *  2. If the server still refuses a row, the count that SUCCEEDED is reported
 *     verbatim and the un-added rows stay in the form. A batch writer that says
 *     "failed" after writing three of five is how a rep ends up with a grid she
 *     did not build and cannot see.
 */
function TierBatchAddForm(props: {
  itemId: string;
  existingQuantities: number[];
  onCancel: () => void;
  /** Fired whenever at least one tier landed — including a PARTIAL run — so
   *  the table refreshes to show exactly what is really there. */
  onAdded: () => void;
  /** Fired only when every planned row landed. */
  onComplete: () => void;
}) {
  const { itemId, existingQuantities, onCancel, onAdded, onComplete } = props;
  const nextKey = useRef(0);
  const blankRow = (): DraftRow => ({
    key: `d${nextKey.current++}`,
    quantity: "",
    wholesale: "",
    sell: "",
    hidden: false,
  });
  const [drafts, setDrafts] = useState<DraftRow[]>(() =>
    Array.from({ length: INITIAL_DRAFT_ROWS }, () => ({
      key: `d${nextKey.current++}`,
      quantity: "",
      wholesale: "",
      sell: "",
      hidden: false,
    })),
  );
  const [errors, setErrors] = useState<TierBatchError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const setRow = (i: number, patch: Partial<TierDraft>): void => {
    setDrafts((prev) =>
      prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)),
    );
    // Clear only the touched row's errors — leaving stale red on rows she has
    // not revisited reads as "still broken" after she has fixed it.
    setErrors((prev) => prev.filter((e) => e.row !== i));
  };

  const addRow = (): void => setDrafts((prev) => [...prev, blankRow()]);

  const removeRow = (i: number): void => {
    setDrafts((prev) =>
      prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i),
    );
    setErrors([]);
  };

  /** The EQP case at entry time: one net cost across every break. Copies the
   *  FIRST row's cost down — the rep types it once. */
  const copyFirstCostDown = (): void => {
    setDrafts((prev) => {
      const first = prev[0]?.wholesale ?? "";
      if (first.trim() === "") return prev;
      return prev.map((d) => ({ ...d, wholesale: first }));
    });
    setErrors((prev) => prev.filter((e) => e.field !== "wholesale"));
  };

  const rowErrors = (i: number): TierBatchError[] =>
    errors.filter((e) => e.row === i);

  const batchError = errors.find((e) => e.row === -1);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setServerError(null);
    const plan = planTierBatch({ drafts, existingQuantities });
    if (!plan.ok) {
      setErrors(plan.errors);
      return;
    }
    setErrors([]);
    setPending(true);
    let added = 0;
    try {
      for (const body of plan.writes) {
        await api(`/presentation_items/${itemId}/tiers`, {
          method: "POST",
          body: JSON.stringify(body),
        });
        added += 1;
      }
      onAdded();
      onComplete();
    } catch (err) {
      // Honest partial report. `added` rows are already live on the quote —
      // drop them from the form so a retry cannot double-write them, and leave
      // the rest so she does not retype what never landed.
      const failed = plan.writes[added];
      const written = plan.writes.slice(0, added).map((w) => w.quantity);
      setDrafts((prev) =>
        prev.filter((d) => {
          const q = Number.parseInt(d.quantity.trim(), 10);
          return !written.includes(q);
        }),
      );
      setServerError(
        added === 0
          ? extractError(err)
          : `Added ${added} of ${plan.writes.length} tiers. The ${
              failed ? `${failed.quantity.toLocaleString()}+ ` : ""
            }tier was refused: ${extractError(err)} The tiers already added are saved; the rest are still below.`,
      );
      if (added > 0) onAdded();
    } finally {
      setPending(false);
    }
  };

  const filledRows = drafts.filter(
    (d) =>
      d.quantity.trim() !== "" ||
      d.wholesale.trim() !== "" ||
      d.sell.trim() !== "",
  ).length;

  return (
    <form
      onSubmit={(e) => {
        void submit(e);
      }}
      className="mt-3 rounded-md border border-peach-300/70 bg-white p-3"
    >
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
          Add price breaks
        </p>
        <button
          type="button"
          onClick={copyFirstCostDown}
          disabled={(drafts[0]?.wholesale ?? "").trim() === ""}
          title="Same net cost at every quantity (EQP) — copy the first row's wholesale to every row below"
          className="inline-flex items-center gap-1 text-xs font-semibold text-brown-400 underline hover:text-rust-500 disabled:no-underline disabled:opacity-40"
        >
          <ArrowDown className="h-3 w-3" />
          Copy first cost to all rows
        </button>
      </div>

      {/* One header row of labels, not a label per input — five stacked rows
          each repeating "Quantity / Wholesale / Sell" reads as five separate
          forms rather than one grid. */}
      <div className={cn(TIER_DRAFT_GRID, "mb-1 hidden sm:grid")}>
        {["Quantity", "Wholesale", "Sell"].map((h) => (
          <span
            key={h}
            className="text-[10px] font-semibold uppercase tracking-wide text-brown-300"
          >
            {h}
          </span>
        ))}
        <span className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
          Internal
        </span>
        <span className="sr-only">Remove</span>
      </div>

      <div className="space-y-2">
        {drafts.map((d, i) => (
          <div key={d.key}>
            <div className={cn(TIER_DRAFT_GRID, "items-center")}>
              <TierDraftInput
                label={`Quantity, row ${i + 1}`}
                value={d.quantity}
                onChange={(v) => setRow(i, { quantity: v })}
                placeholder="50"
                autoFocus={i === 0}
                invalid={rowErrors(i).some((e) => e.field === "quantity")}
              />
              <TierDraftInput
                label={`Wholesale cost, row ${i + 1}`}
                value={d.wholesale}
                onChange={(v) => setRow(i, { wholesale: v })}
                placeholder="4.25"
                invalid={rowErrors(i).some((e) => e.field === "wholesale")}
              />
              <TierDraftInput
                label={`Sell price, row ${i + 1}`}
                value={d.sell}
                onChange={(v) => setRow(i, { sell: v })}
                placeholder="9.95"
                invalid={rowErrors(i).some((e) => e.field === "sell")}
              />
              <input
                type="checkbox"
                checked={d.hidden ?? false}
                onChange={(e) => setRow(i, { hidden: e.target.checked })}
                aria-label={`Internal only, row ${i + 1}`}
                className="h-4 w-4 rounded border-brown-200 text-ink underline underline-offset-4 focus:ring-peach-300"
              />
              <button
                type="button"
                onClick={() => removeRow(i)}
                disabled={drafts.length === 1}
                aria-label={`Remove row ${i + 1}`}
                className="rounded-md p-1 text-brown-300 hover:bg-rust-50 hover:text-rust-500 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-brown-300"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            {rowErrors(i).map((e) => (
              <p key={e.field} className="mt-0.5 text-xs text-rust-600">
                {e.message}
              </p>
            ))}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addRow}
        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brown-400 underline hover:text-brown-500"
      >
        <Plus className="h-3 w-3" />
        Add another row
      </button>

      {batchError && (
        <p className="mt-2 text-xs text-rust-600">{batchError.message}</p>
      )}
      {serverError && (
        <p className="mt-2 text-xs text-rust-600">{serverError}</p>
      )}

      <div className="mt-3 flex items-center justify-end gap-2">
        <span className="mr-auto text-[10px] text-brown-300">
          Blank rows are ignored.
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={pending}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={pending}>
          {pending
            ? "Adding…"
            : filledRows > 1
              ? `Add ${filledRows} tiers`
              : "Add tier"}
        </Button>
      </div>
    </form>
  );
}

// TierEditRow was removed 2026-08-18. The tier grid is transposed — one
// COLUMN per quantity break, every cell live — so there is no open-edit-save
// cycle left for it to serve. Its validation moved into TierColumnGrid's
// per-cell `validate`, unchanged: quantity a positive integer, cost 3dp via
// isValidCostInput, sell 2dp.

// NumberField (label-above-input) lived here for the old single-row tier add
// form. The batch form uses one header row of column labels instead — five
// stacked rows each repeating "Quantity / Wholesale / Sell" reads as five
// separate forms rather than one grid — so its inputs are TierDraftInput.

// ─── Custom-product affordance (presentation add-modal footer slot) ───
// Passed into <AddProductModal customProductSlot>. Owns the "Can't find it?
// Add custom product" toggle and renders the unchanged <CustomProductForm> for
// items outside the synced feeds (local decorators, closeouts, client-supplied
// goods). 2026-07-14; re-homed into the shared modal 2026-07-29.

function CustomProductAffordance(props: {
  presentationId: string;
  targetSectionId: string | null;
  onItemAdded: () => void;
}) {
  const { presentationId, targetSectionId, onItemAdded } = props;
  const [showCustom, setShowCustom] = useState(false);
  // Auto-add to the presentation via the existing add-item path, then file it
  // under the section the rep opened the modal FROM (a failure there leaves
  // the product on the presentation, ungrouped — visible and fixable).
  async function addToPresentation(created: CreatedCustomProduct): Promise<string | null> {
    const addedItem = await api<{ item: { id: string } }>(
      `/presentations/${presentationId}/items`,
      {
        method: "POST",
        body: JSON.stringify({ product_id: created.product_id }),
      },
    );
    if (targetSectionId !== null) {
      try {
        await api(`/presentation_items/${addedItem.item.id}/section`, {
          method: "PATCH",
          body: JSON.stringify({ section_id: targetSectionId }),
        });
      } catch {
        return "Product added, but it could not be filed under that section. Move it with the section dropdown on the card.";
      }
    }
    return null;
  }
  return showCustom ? (
    <CustomProductForm
      onCreated={addToPresentation}
      addedText={targetSectionId !== null ? "Added under this category." : "Added to the presentation."}
      addLabel="Add to presentation"
      onItemAdded={onItemAdded}
      onClose={() => setShowCustom(false)}
    />
  ) : (
    <button
      type="button"
      onClick={() => setShowCustom(true)}
      className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:text-muted"
    >
      <Plus className="h-4 w-4" />
      Can't find it? Add custom product
    </button>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────

function extractError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    // Prefer friendly copy for known stable codes over the raw body — the
    // duplicate-item 409 returns a machine code, not a client-safe sentence.
    const friendly = friendlyApiError(body?.error);
    if (friendly) return friendly;
    return body?.message ?? body?.error ?? `Request failed (${err.status}).`;
  }
  return "Request failed. Try again or check your connection.";
}

function HeaderFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-ink">{value}</dd>
    </div>
  );
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (y === undefined || m === undefined || d === undefined) return iso;
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const monthName = months[Number.parseInt(m, 10) - 1] ?? m;
  return `${monthName} ${Number.parseInt(d, 10)}, ${y}`;
}

// ─── Client Feedback panel (PR #113) ─────────────────────────────────
// Surfaces the most-recent client submission(s) from /p/:uuid/feedback.
// Shows comment text, viewer email, timestamp, total thumb counts, and
// per-item thumb counts inline next to the items list (the panel sits
// above the items section).

function ClientFeedbackPanel(props: {
  feedback: ClientFeedbackSummary;
  items: PresentationItem[];
}) {
  const { feedback, items } = props;
  const itemComments = feedback.itemComments ?? [];
  const mostRecent = feedback.recentSubmissions[0];
  // 2026-07-22 — the redesigned page produces per-item comments (no thumbs).
  // 2026-07-26 (B6) — a selections-only submission carries NO thumbs, NO
  // overall comment, and NO per-item notes; it must STILL render (that's the
  // headline B6 scenario), so clientSelections is a third render trigger.
  // Render the panel whenever there's a thumbs-era submission OR at least one
  // per-item note OR the client made item picks.
  if (
    !mostRecent &&
    itemComments.length === 0 &&
    feedback.clientSelections == null
  )
    return null;

  const productNamesById = new Map<string, string>();
  for (const it of items) {
    const snap = (it.productSnapshot as { name?: string } | null) ?? {};
    if (snap.name) productNamesById.set(it.id, snap.name);
  }
  const flaggedItems = feedback.thumbsByItem
    .filter((t) => t.likes > 0 || t.dislikes > 0)
    .map((t) => ({
      name: productNamesById.get(t.presentationItemId) ?? "Item",
      likes: t.likes,
      dislikes: t.dislikes,
    }));
  // Group the (already newest-first) notes by item, preserving order within
  // each group. Resubmission is supported, so an item can carry several notes;
  // each keeps its own relative timestamp so stale notes aren't shown as if
  // current. The service caps the array at 50 and reports the full count.
  const itemCommentGroups: Array<{
    presentationItemId: string;
    name: string;
    notes: Array<{ comment: string; submittedAt: string }>;
  }> = [];
  const groupIndexById = new Map<string, number>();
  for (const c of itemComments) {
    let idx = groupIndexById.get(c.presentationItemId);
    if (idx === undefined) {
      idx = itemCommentGroups.length;
      groupIndexById.set(c.presentationItemId, idx);
      itemCommentGroups.push({
        presentationItemId: c.presentationItemId,
        name: productNamesById.get(c.presentationItemId) ?? "Item",
        notes: [],
      });
    }
    const group = itemCommentGroups[idx];
    if (group) {
      group.notes.push({ comment: c.comment, submittedAt: c.submittedAt });
    }
  }
  const renderedNoteCount = itemComments.length;
  const totalNoteCount = feedback.itemCommentsTotal ?? renderedNoteCount;
  const earlierNoteCount = Math.max(0, totalNoteCount - renderedNoteCount);
  const headerMeta = mostRecent ?? itemComments[0] ?? null;

  return (
    <section className="rounded-md border border-warn/30 bg-warn-bg p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
          Client feedback
        </h2>
        {headerMeta && (
          <span className="text-xs text-brown-300">
            {headerMeta.viewerEmail ? `${headerMeta.viewerEmail} · ` : ""}
            {formatRelative(headerMeta.submittedAt)}
          </span>
        )}
      </div>

      {/* Total counts */}
      {(feedback.totalLikes > 0 || feedback.totalDislikes > 0) && (
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {feedback.totalLikes > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-peach-100 px-3 py-1 font-semibold text-brown-500">
              <ThumbsUp className="h-3 w-3" /> {feedback.totalLikes} liked
            </span>
          )}
          {feedback.totalDislikes > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-brown-100 px-3 py-1 font-semibold text-brown-500">
              <ThumbsDown className="h-3 w-3" /> {feedback.totalDislikes} passed
            </span>
          )}
        </div>
      )}

      {/* Overall comment (legacy thumbs-era page) */}
      {mostRecent?.comment && (
        <blockquote className="mt-4 whitespace-pre-wrap border-l-4 border-warn bg-white px-4 py-3 text-sm leading-relaxed text-brown-500">
          {mostRecent.comment}
        </blockquote>
      )}

      {/* Client's latest picks (B6) — the LATEST selection set, above
          Notes-by-item. Names resolve rep-side; the timestamp + optional viewer
          email attribute the pick. Convert pre-checks these same items. When
          the client re-picked (>1 submission carried selections) we show only
          the latest set but tell the rep how many rounds arrived. */}
      {feedback.clientSelections &&
        feedback.clientSelections.presentationItemIds.length > 0 && (
          <div className="mt-4">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
              Client's latest picks ·{" "}
              {formatRelative(feedback.clientSelections.submittedAt)}
              {feedback.clientSelections.viewerEmail
                ? ` · ${feedback.clientSelections.viewerEmail}`
                : ""}
            </p>
            {feedback.clientSelections.submissionCount > 1 && (
              <p className="mt-1 text-xs text-brown-400">
                {feedback.clientSelections.submissionCount} submissions received
                — showing the latest picks
              </p>
            )}
            <ul className="mt-2 flex flex-wrap gap-2">
              {feedback.clientSelections.presentationItemIds.map((pid) => (
                <li
                  key={pid}
                  className="inline-flex items-center rounded-full bg-peach-100 px-3 py-1 text-xs font-semibold text-brown-500"
                >
                  {productNamesById.get(pid) ?? "Item"}
                </li>
              ))}
            </ul>
          </div>
        )}

      {/* Per-item notes (redesigned page) — grouped by item, newest-first,
          each note timestamped. Capped at 50 with a "+N earlier notes" line. */}
      {itemCommentGroups.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
            Notes by item
          </p>
          <ul className="mt-2 space-y-2">
            {itemCommentGroups.map((g) => (
              <li
                key={g.presentationItemId}
                className="border-l-4 border-warn bg-white px-4 py-3"
              >
                <p className="text-xs font-semibold text-brown-500">{g.name}</p>
                <ul className="mt-1 space-y-2">
                  {g.notes.map((n, j) => (
                    <li key={j}>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-brown-500">
                        {n.comment}
                      </p>
                      <p className="mt-0.5 text-[10px] text-brown-300">
                        {formatRelative(n.submittedAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          {earlierNoteCount > 0 && (
            <p className="mt-2 text-[10px] text-brown-300">
              + {earlierNoteCount} earlier note
              {earlierNoteCount === 1 ? "" : "s"}
            </p>
          )}
        </div>
      )}

      {/* Per-item breakdown */}
      {flaggedItems.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
            By item
          </p>
          <ul className="mt-2 space-y-1 text-xs text-brown-400">
            {flaggedItems.map((f, i) => (
              <li key={i} className="flex items-center justify-between gap-3">
                <span className="truncate">{f.name}</span>
                <span className="flex items-center gap-2 text-brown-300">
                  {f.likes > 0 && (
                    <span className="inline-flex items-center gap-0.5">
                      <ThumbsUp className="h-3 w-3 text-muted" /> {f.likes}
                    </span>
                  )}
                  {f.dislikes > 0 && (
                    <span className="inline-flex items-center gap-0.5">
                      <ThumbsDown className="h-3 w-3 text-brown-400" />{" "}
                      {f.dislikes}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {feedback.recentSubmissions.length > 1 && (
        <p className="mt-4 text-[10px] text-brown-300">
          + {feedback.recentSubmissions.length - 1} earlier submission
          {feedback.recentSubmissions.length - 1 === 1 ? "" : "s"}
        </p>
      )}
    </section>
  );
}

function formatRelative(iso: string): string {
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const min = Math.round(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    if (day < 30) return `${day}d ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}
